import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DndContext, DragOverlay, PointerSensor, KeyboardSensor, pointerWithin, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { ArrowDown, ArrowLeft, ChevronDown, ChevronUp, Clock3, Copy, FilePlus2, Loader2, Pencil, Plus, Printer, RotateCcw, Save, Sparkles, Trash2, Droplet, Video, X } from 'lucide-react'
import waterGlassIcon from '@/assets/icons/waterglass.svg'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UnderlineTabs } from '@/components/ui/underline-tabs'
import config from '@/config/config'
import { apiService } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

import {
  formatDate,
  formatNumber,
  diffField,
  toInputDate,
  formatForApiDate,
  toIsoDateString,
  parseNumber,
  isNotFoundError,
  createEmptyMeasurements,
  dayOrder,
  dayLabels,
  dayFieldMap,
  mealNameOptions,
  mealPlanAccents,
  defaultFloatPos,
  defaultLibraryFloatSize,
  defaultTotalsFloatSize,
  toNumberOrZero,
  formatTimeForInput,
  normalizeTimePayload,
  tryParseJSON,
  inferNutritionLibraryNodeKind,
  micronutrientKeyMap,
  micronutrientDisplayOrder,
  micronutrientLabels,
  extractMicronutrients,
  aggregateMicronutrients,
  getProgramIdForRow,
  getWeightId,
  getCircumferenceId,
  getVitalId,
  firstNonEmptyText,
  resolveExerciseInfoText,
  resolveExerciseVideoUrl,
  hasMeaningfulValue,
  getExercisePairField,
  getExercisePairValue,
  EMPTY_OPTIONS,
  EXERCISE_PAIR_DEFS,
  toFieldBadgeLabel,
  formatClientDisplayName,
  getRowKey,
  COMPOSITION_COLUMNS,
  CIRCUMFERENCE_COLUMNS,
  VITALS_COLUMNS,
  toExerciseEmbedUrl,
} from './session-detail/utils/sessionDetailHelpers'
import MeasurementsTable from './session-detail/components/MeasurementsTable'
import SharedDialogs from './session-detail/components/SharedDialogs'
import ExerciseDialogs from './session-detail/components/ExerciseDialogs'
import NutritionDialogs from './session-detail/components/NutritionDialogs'
import NutritionLibraryRail from './session-detail/components/NutritionLibraryRail'
import ExerciseLibraryRail from './session-detail/components/ExerciseLibraryRail'
import NutritionTotalsPanel from './session-detail/components/NutritionTotalsPanel'
import SortableMealItem from './session-detail/components/SortableMealItem'
import SortableExerciseRow from './session-detail/components/SortableExerciseRow'
import {
  MealDropZone,
  LibraryDragOverlay,
  ExerciseDropZone,
  ExerciseLibraryDragOverlay,
} from './session-detail/components/LibraryPrimitives'
import { useFloatingPanel } from './session-detail/hooks/useFloatingPanel'
import { useClientProgramCopy } from './session-detail/hooks/useClientProgramCopy'
import ClientCopyPicker from './session-detail/components/ClientCopyPicker'

// Same rounding as the Angular app's getFixedValue (2 decimals, non-numbers become 0)
const roundTo2 = (value) => {
  const num = Number(value)
  return Number.isFinite(num) ? Number(num.toFixed(2)) : 0
}

const SessionDetail = () => {
  const { clientId, sessionId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { companyUrl, user } = useAuthStore()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [measurements, setMeasurements] = useState({ compositions: [], circumferences: [], vitals: [] })
  const [detailData, setDetailData] = useState({})
  const [activeTab, setActiveTab] = useState('measurements')
  const [filterMode, setFilterMode] = useState('all')
  const [editableCompositions, setEditableCompositions] = useState([])
  const [editableCircumferences, setEditableCircumferences] = useState([])
  const [editableVitals, setEditableVitals] = useState([])
  const [editingRow, setEditingRow] = useState(null)
  const [programKey, setProgramKey] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [measurementsLoading, setMeasurementsLoading] = useState(false)
  const [originalRows, setOriginalRows] = useState({ composition: [], circumference: [], vital: [] })
  const [deleteDialog, setDeleteDialog] = useState(null)
  const [printDialogOpen, setPrintDialogOpen] = useState(false)
  const [printSelection, setPrintSelection] = useState('all')
  const [printLoading, setPrintLoading] = useState(false)
  const [activeNutritionPlanId, setActiveNutritionPlanId] = useState(null)
  const [nutritionPlans, setNutritionPlans] = useState([])
  const [mealWaterCounts, setMealWaterCounts] = useState({})
  const [libraryTree, setLibraryTree] = useState([])
  const [libraryFilter, setLibraryFilter] = useState('')
  const [libraryExpanded, setLibraryExpanded] = useState({})
  const [exercisePlans, setExercisePlans] = useState([])
  const [activeExercisePlanId, setActiveExercisePlanId] = useState(null)

  const activeNutritionPlan = useMemo(
    () => nutritionPlans.find((plan) => plan.id === activeNutritionPlanId) || null,
    [nutritionPlans, activeNutritionPlanId]
  )

  const activeExercisePlan = useMemo(
    () => exercisePlans.find((plan) => plan.id === activeExercisePlanId) || null,
    [exercisePlans, activeExercisePlanId]
  )
  const [exerciseLibraryTree, setExerciseLibraryTree] = useState([])
  const [exerciseLibraryFilter, setExerciseLibraryFilter] = useState('')
  const [exerciseLibraryExpanded, setExerciseLibraryExpanded] = useState({})
  const [exerciseCategoryOptions, setExerciseCategoryOptions] = useState([])
  const [activeDragNode, setActiveDragNode] = useState(null)
  const [mealDragErrors, setMealDragErrors] = useState({})
  const [mealDragLoading, setMealDragLoadingState] = useState({})
  const [activeExerciseDrag, setActiveExerciseDrag] = useState(null)
  const [planDragErrors, setPlanDragErrors] = useState({})
  const [planDragLoading, setPlanDragLoadingState] = useState({})
  const [exerciseDeleteDialog, setExerciseDeleteDialog] = useState(null)
  const [exerciseDeleteLoading, setExerciseDeleteLoading] = useState(false)
  const [exerciseInfoDialog, setExerciseInfoDialog] = useState(null)
  const [exerciseVideoDialog, setExerciseVideoDialog] = useState({ open: false, name: '', embedUrl: '', rawUrl: '' })
  const [exerciseDaySaving, setExerciseDaySaving] = useState({})
  const [exercisePlanNameDrafts, setExercisePlanNameDrafts] = useState({})
  const [exercisePlanNameSaving, setExercisePlanNameSaving] = useState({})
  const [exercisePlanReorderSaving, setExercisePlanReorderSaving] = useState({})
  const [exerciseActionLoading, setExerciseActionLoading] = useState({})
  const [exerciseTemplateOptionsByRow, setExerciseTemplateOptionsByRow] = useState({})
  const [exerciseTemplateLoadingByRow, setExerciseTemplateLoadingByRow] = useState({})
  const [createExercisePlanLoading, setCreateExercisePlanLoading] = useState(false)
  const [exerciseFieldSaving, setExerciseFieldSaving] = useState({})
  const [exercisePlanDeleteDialog, setExercisePlanDeleteDialog] = useState(null)
  const [exercisePlanDeleteLoading, setExercisePlanDeleteLoading] = useState(false)
  const [exerciseBulkDialog, setExerciseBulkDialog] = useState(null)
  const [exerciseBulkValues, setExerciseBulkValues] = useState({ sets: '', reps: '', rest: '', speed: '' })
  const [exerciseBulkSaving, setExerciseBulkSaving] = useState(false)
  const [exerciseBulkError, setExerciseBulkError] = useState('')
  const [mealDeleteDialog, setMealDeleteDialog] = useState(null)
  const [mealDeleteLoading, setMealDeleteLoading] = useState(false)
  const [mealPlanDeleteDialog, setMealPlanDeleteDialog] = useState(null)
  const [mealPlanDeleteLoading, setMealPlanDeleteLoading] = useState(false)
  const [pdfLoading, setPdfLoading] = useState({ meals: false, shopping: false, recipes: false, exercises: false })
  const [addMealLoading, setAddMealLoading] = useState(false)
  const [mealNoteDialog, setMealNoteDialog] = useState(null)
  const [mealNoteDraft, setMealNoteDraft] = useState('')
  const [mealNoteSaving, setMealNoteSaving] = useState(false)
  const [mealNoteError, setMealNoteError] = useState('')
  const [mealPlanDaySaving, setMealPlanDaySaving] = useState({})
  const [activeMicronutrientMeal, setActiveMicronutrientMeal] = useState(null)
  const [showTotalsMicros, setShowTotalsMicros] = useState(false)
  const [planNameEditId, setPlanNameEditId] = useState(null)
  const [planNameDraft, setPlanNameDraft] = useState('')
  const [planNameSaving, setPlanNameSaving] = useState(false)
  const [planNameError, setPlanNameError] = useState('')
  const [planDeleteDialog, setPlanDeleteDialog] = useState(null)
  const [planDeleteLoading, setPlanDeleteLoading] = useState(false)

  const [newPlanName, setNewPlanName] = useState('')
  const [newPlanSaving, setNewPlanSaving] = useState(false)
  const [newPlanError, setNewPlanError] = useState('')
  const [mealItemSearchTerms, setMealItemSearchTerms] = useState({})
  const [mealItemSuggestions, setMealItemSuggestions] = useState({})
  const [mealItemSearchLoading, setMealItemSearchLoading] = useState({})
  const [mealItemSaving, setMealItemSaving] = useState({})
  const [mealItemCopyLoading, setMealItemCopyLoading] = useState({})
  const [mealHeaderSaving, setMealHeaderSaving] = useState({})
  const libraryPanelRef = useRef(null)
  const sideRailAnchorRef = useRef(null)
  const mealItemSearchDebounce = useRef({})
  const micronutrientCloseTimeoutRef = useRef(null)

  const totalsPanelRef = useRef(null)
  // eslint-disable-next-line no-unused-vars
  const library = useFloatingPanel(libraryPanelRef, { defaultPos: defaultFloatPos, defaultSize: defaultLibraryFloatSize })
  // eslint-disable-next-line no-unused-vars
  const totals = useFloatingPanel(totalsPanelRef, { defaultPos: defaultFloatPos, defaultSize: defaultTotalsFloatSize })
  const [desktopRailStyle, setDesktopRailStyle] = useState(null)
  const libraryExpandedRef = useRef({})
  const librarySearchSnapshotRef = useRef(null)
  const librarySearchActiveRef = useRef(false)
  const exercisePlansRef = useRef([])
  const exerciseLibraryExpandedRef = useRef({})
  const exerciseLibrarySearchSnapshotRef = useRef(null)
  const exerciseLibrarySearchActiveRef = useRef(false)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )
  const libraryPortalTarget = typeof document !== 'undefined' ? document.body : null

  const clearMicronutrientCloseTimeout = useCallback(() => {
    if (micronutrientCloseTimeoutRef.current) {
      clearTimeout(micronutrientCloseTimeoutRef.current)
      micronutrientCloseTimeoutRef.current = null
    }
  }, [])

  const openMicronutrientPopover = useCallback((mealId) => {
    clearMicronutrientCloseTimeout()
    setActiveMicronutrientMeal(mealId)
  }, [clearMicronutrientCloseTimeout])

  const scheduleMicronutrientPopoverClose = useCallback((mealId) => {
    clearMicronutrientCloseTimeout()
    micronutrientCloseTimeoutRef.current = setTimeout(() => {
      setActiveMicronutrientMeal((current) => (current === mealId ? null : current))
      micronutrientCloseTimeoutRef.current = null
    }, 120)
  }, [clearMicronutrientCloseTimeout])

  useEffect(() => () => clearMicronutrientCloseTimeout(), [clearMicronutrientCloseTimeout])

  const collisionDetection = useCallback(
    (args) => {
      const activeType = args.active?.data?.current?.type
      const isLibraryDragType = activeType === 'libraryItem' || activeType === 'exerciseLibraryItem'
      if (isLibraryDragType && args.pointerCoordinates && libraryPanelRef.current) {
        const rect = libraryPanelRef.current.getBoundingClientRect()
        const { x, y } = args.pointerCoordinates
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          return []
        }
      }
      return pointerWithin(args)
    },
    [libraryPanelRef]
  )

  const refreshMealPlans = useCallback(
    async (signal = null) => {
      if (!companyUrl || !clientId) return []
      const sessionMeta = location.state?.session || {}
      const programValue =
        programKey ||
        sessionMeta.sessionProgramId ||
        sessionMeta.program_id ||
        sessionMeta.programId ||
        sessionMeta.programid ||
        sessionMeta.program ||
        sessionId ||
        location.state?.programId
      if (!programValue) return []
      const res = await apiService.post(`/${companyUrl}/getprogrammealplans`, { program_id: programValue, client_id: clientId }, signal)
      if (res.success) {
        setDetailData((prev) => ({ ...prev, mealPlans: res.data }))
        return Array.isArray(res.data) ? res.data : []
      }
      if (isNotFoundError(res.error)) {
        setDetailData((prev) => ({ ...prev, mealPlans: [] }))
        return []
      }
      setError((prev) => prev || res.error || 'Unable to refresh meal plans')
      return []
    },
    [clientId, companyUrl, location.state, programKey, sessionId]
  )

  const refreshExercisePlans = useCallback(
    async (signal = null) => {
      if (!companyUrl || !sessionId || !clientId) return
      const res = await apiService.post(
        `/${companyUrl}/exercise/getsessionexerciseplans`,
        { session_id: sessionId, client_id: clientId },
        signal
      )
      if (res.success) {
        setDetailData((prev) => ({ ...prev, sessionExercisePlans: res.data }))
      } else if (isNotFoundError(res.error)) {
        setDetailData((prev) => ({ ...prev, sessionExercisePlans: [] }))
      } else {
        setError((prev) => prev || res.error || 'Unable to refresh exercise plans')
      }
    },
    [clientId, companyUrl, sessionId]
  )

  const refreshGoalSessions = useCallback(
    async (signal = null) => {
      if (!companyUrl) return
      const sessionMeta = location.state?.session || {}
      const programValue =
        programKey ||
        sessionMeta.sessionProgramId ||
        sessionMeta.program_id ||
        sessionMeta.programId ||
        sessionMeta.programid ||
        sessionMeta.program ||
        sessionId ||
        location.state?.programId
      const goalValue = location.state?.goalId || sessionMeta.goal_id || location.state?.programId || programValue
      if (!goalValue) return
      try {
        const res = await apiService.post(`/${companyUrl}/getgoalsessions`, { goal_id: goalValue }, signal)
        if (res.success) {
          setDetailData((prev) => ({ ...prev, goalSessions: res.data }))
        } else if (isNotFoundError(res.error)) {
          setDetailData((prev) => ({ ...prev, goalSessions: [] }))
        } else {
          setError((prev) => prev || res.error || 'Unable to refresh goal sessions')
        }
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError((prev) => prev || 'Unable to refresh goal sessions')
      }
    },
    [companyUrl, location.state, programKey, sessionId]
  )

  const handleMealDeleteRequest = (item, mealId, planId, mealTitle) => {
    setMealDeleteDialog({ item, mealId, planId, mealTitle })
  }

  const handleMealPlanDeleteRequest = useCallback((meal, plan) => {
    if (!meal || !plan) return
    setMealPlanDeleteDialog({ meal, plan })
  }, [])

  const handleConfirmMealPlanDelete = useCallback(async () => {
    if (!mealPlanDeleteDialog) return
    const planId = mealPlanDeleteDialog.plan?.apiId || mealPlanDeleteDialog.plan?.id
    const mealId = mealPlanDeleteDialog.meal?.apiId || mealPlanDeleteDialog.meal?.id
    if (!companyUrl || !planId || !mealId) {
      setMealPlanDeleteDialog(null)
      setError((prev) => prev || 'Unable to delete meal.')
      return
    }
    setMealPlanDeleteLoading(true)
    try {
      const res = await apiService.delete(`/${companyUrl}/deletemeal/${planId}/${mealId}`)
      if (!res.success) {
        setError((prev) => prev || res.error || 'Unable to delete meal.')
        return
      }
      await refreshMealPlans()
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        setError((prev) => prev || 'Unable to delete meal.')
      }
    } finally {
      setMealPlanDeleteLoading(false)
      setMealPlanDeleteDialog(null)
    }
  }, [companyUrl, mealPlanDeleteDialog, refreshMealPlans])

  const handleMealNoteOpen = useCallback((item, mealId, planId) => {
    setMealNoteDialog({ item, mealId, planId })
    setMealNoteDraft(item?.note || '')
    setMealNoteError('')
  }, [])

  const handleMealNoteClose = useCallback((force = false) => {
    if (mealNoteSaving && !force) return
    setMealNoteDialog(null)
    setMealNoteDraft('')
    setMealNoteError('')
  }, [mealNoteSaving])

  const handleMealNoteSave = useCallback(async () => {
    if (!mealNoteDialog?.item) return
    const { item } = mealNoteDialog
    if (!companyUrl) {
      setMealNoteError('Missing company context.')
      return
    }
    if (!item.mealfoodId) {
      setMealNoteError('Unable to save note for this item.')
      return
    }
    const mealIdValue = (() => {
      const raw = item.mealId ?? mealNoteDialog.mealId
      const numeric = Number(raw)
      return Number.isFinite(numeric) ? numeric : raw
    })()
    if (!mealIdValue) {
      setMealNoteError('Missing meal id for this item.')
      return
    }
    const payload = {
      meal_id: mealIdValue,
      name: item.name || '',
      qty: toNumberOrZero(item.qty),
      calories: toNumberOrZero(item.calories),
      carbs: toNumberOrZero(item.carbs),
      unit: item.unit || '',
      protein: toNumberOrZero(item.protein),
      fat: toNumberOrZero(item.fat),
      note: mealNoteDraft || '',
    }
    setMealNoteSaving(true)
    setMealNoteError('')
    try {
      const res = await apiService.put(`/${companyUrl}/updatemealfood/${item.mealfoodId}`, payload)
      if (!res.success) {
        setMealNoteError(res.error || 'Unable to save note.')
        return
      }
      setNutritionPlans((prev) =>
        prev.map((plan) => ({
          ...plan,
          meals: (plan.meals || []).map((meal) => {
            const matchesMeal = meal.id === mealNoteDialog.mealId || meal.apiId === item.mealId || meal.id === item.mealId
            if (!matchesMeal) return meal
            return {
              ...meal,
              items: (meal.items || []).map((food) =>
                food.id === item.id ? { ...food, note: mealNoteDraft } : food
              ),
            }
          }),
        }))
      )
      handleMealNoteClose(true)
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        setMealNoteError('Unable to save note.')
      }
    } finally {
      setMealNoteSaving(false)
    }
  }, [companyUrl, handleMealNoteClose, mealNoteDialog, mealNoteDraft])

  const updateMealItemLocal = useCallback((itemId, mealIdValue, updater) => {
    setNutritionPlans((prev) =>
      prev.map((plan) => ({
        ...plan,
        meals: (plan.meals || []).map((meal) => {
          if (meal.id !== mealIdValue && meal.apiId !== mealIdValue) return meal
          return {
            ...meal,
            items: (meal.items || []).map((food) => (food.id === itemId ? updater(food) : food)),
          }
        }),
      }))
    )
  }, [])

  const updateMealHeaderLocal = useCallback((mealIdValue, updater) => {
    setNutritionPlans((prev) =>
      prev.map((plan) => ({
        ...plan,
        meals: (plan.meals || []).map((meal) => {
          const matches = meal.id === mealIdValue || meal.apiId === mealIdValue
          if (!matches) return meal
          return typeof updater === 'function' ? updater(meal) : { ...meal, ...updater }
        }),
      }))
    )
  }, [])

  const saveMealItem = useCallback(
    async (item, mealIdValue, allOverrides = {}) => {
      // foodDetail = full /nutrition/nutrients (or searchbyscan) result, sent along when the food is first added
      const { foodDetail, ...overrides } = allOverrides
      if (!companyUrl) {
        setError((prev) => prev || 'Missing company context.')
        return false
      }
      const resolvedMealId = (() => {
        const raw = mealIdValue ?? item.mealId
        const numeric = Number(raw)
        return Number.isFinite(numeric) ? numeric : raw
      })()
      if (!resolvedMealId) {
        setError((prev) => prev || 'Missing meal id.')
        return false
      }
      const resolvedName = overrides.name ?? item.name ?? ''
      const resolvedQty = toNumberOrZero(overrides.qty ?? item.qty)
      const resolvedCalories = toNumberOrZero(overrides.calories ?? item.calories)
      const resolvedCarbs = toNumberOrZero(overrides.carbs ?? item.carbs)
      const resolvedProtein = toNumberOrZero(overrides.protein ?? item.protein)
      const resolvedFat = toNumberOrZero(overrides.fat ?? item.fat)
      const resolvedUnit = overrides.unit ?? item.unit ?? ''

      setMealItemSaving((prev) => ({ ...prev, [item.id]: true }))
      try {
        if (!item?.mealfoodId) {
          const resolvedMealPlanId = (() => {
            const raw = activeNutritionPlan?.apiId ?? activeNutritionPlan?.id
            const numeric = Number(raw)
            return Number.isFinite(numeric) ? numeric : raw
          })()
          const payload = foodDetail
            ? {
                meal_id: resolvedMealId,
                mealplan_id: resolvedMealPlanId,
                ...foodDetail,
                name: foodDetail.name || foodDetail.food_name || resolvedName,
                calories: roundTo2(foodDetail.calories),
                carbs: roundTo2(foodDetail.carbs),
                protein: roundTo2(foodDetail.protein),
                fat: roundTo2(foodDetail.fat),
              }
            : {
                meal_id: resolvedMealId,
                mealplan_id: resolvedMealPlanId,
                displayName: resolvedName,
                enabled: true,
                name: resolvedName,
                foodoptions: [],
                qty: String(resolvedQty),
                is_added_manual: 1,
                calories: resolvedCalories,
                carbs: resolvedCarbs,
                protein: resolvedProtein,
                fat: resolvedFat,
              }
          const res = await apiService.post(`/${companyUrl}/addmealfood`, payload)
          if (!res.success) {
            setError((prev) => prev || res.error || 'Unable to add meal item')
            return false
          }
          const created = res.data || {}
          setNutritionPlans((prev) =>
            prev.map((plan) => ({
              ...plan,
              meals: (plan.meals || []).map((meal) => {
                if (meal.id !== mealIdValue && meal.apiId !== mealIdValue) return meal
                return {
                  ...meal,
                  items: (meal.items || []).map((food) =>
                    food.id === item.id
                      ? {
                          id: String(created.id || food.id),
                          mealfoodId: created.id || null,
                          mealId: resolvedMealId,
                          name: created.name || resolvedName,
                          qty: created.qty ?? resolvedQty,
                          unit: created.unit || resolvedUnit,
                          calories: toNumberOrZero(created.calories ?? resolvedCalories),
                          protein: toNumberOrZero(created.protein ?? resolvedProtein),
                          carbs: toNumberOrZero(created.carbs ?? resolvedCarbs),
                          fat: toNumberOrZero(created.fat ?? resolvedFat),
                          note: created.note || created.clientnote || '',
                          micronutrients: extractMicronutrients(created),
                        }
                      : food
                  ),
                }
              }),
            }))
          )
          return true
        }

        const payload = {
          meal_id: resolvedMealId,
          name: resolvedName,
          qty: resolvedQty,
          calories: resolvedCalories,
          carbs: resolvedCarbs,
          unit: resolvedUnit,
          protein: resolvedProtein,
          fat: resolvedFat,
          note: overrides.note ?? item.note ?? '',
        }
        const res = await apiService.put(`/${companyUrl}/updatemealfood/${item.mealfoodId}`, payload)
        if (!res.success) {
          setError((prev) => prev || res.error || 'Unable to update meal item')
          return false
        }
        updateMealItemLocal(item.id, mealIdValue, (food) => ({ ...food, ...overrides }))
        return true
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          setError((prev) => prev || 'Unable to save meal item')
        }
        return false
      } finally {
        setMealItemSaving((prev) => {
          const next = { ...prev }
          delete next[item.id]
          return next
        })
      }
    },
    [activeNutritionPlan, companyUrl, updateMealItemLocal]
  )

  const fetchMealItemSuggestions = useCallback(
    async (itemId, term) => {
      if (!term?.trim()) {
        setMealItemSuggestions((prev) => {
          const next = { ...prev }
          delete next[itemId]
          return next
        })
        setMealItemSearchLoading((prev) => {
          const next = { ...prev }
          delete next[itemId]
          return next
        })
        return
      }
      if (!companyUrl) return
      setMealItemSearchLoading((prev) => ({ ...prev, [itemId]: true }))
      try {
        const res = await apiService.searchNutrition(companyUrl, term)
        if (res.success) {
          const results = res.data?.results || res.data?.resultsv2?.branded || []
          setMealItemSuggestions((prev) => ({ ...prev, [itemId]: results }))
        }
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          setError((prev) => prev || 'Unable to search food')
        }
      } finally {
        setMealItemSearchLoading((prev) => ({ ...prev, [itemId]: false }))
      }
    },
    [companyUrl]
  )

  const handleMealItemNameChange = useCallback(
    (item, mealIdValue, planId, value) => {
      updateMealItemLocal(item.id, mealIdValue, (food) => ({ ...food, name: value }))
      setMealItemSearchTerms((prev) => ({ ...prev, [item.id]: value }))
      const debounceMap = mealItemSearchDebounce.current || {}
      if (debounceMap[item.id]) {
        clearTimeout(debounceMap[item.id])
      }
      debounceMap[item.id] = setTimeout(() => {
        fetchMealItemSuggestions(item.id, value)
      }, 300)
      mealItemSearchDebounce.current = debounceMap
    },
    [fetchMealItemSuggestions, updateMealItemLocal]
  )

  const handleMealItemQtyChange = useCallback(
    (item, mealIdValue, planId, value) => {
      updateMealItemLocal(item.id, mealIdValue, (food) => ({ ...food, qty: value }))
    },
    [updateMealItemLocal]
  )

  const handleMealItemQtyBlur = useCallback(
    (item, mealIdValue, planId) => {
      saveMealItem(item, mealIdValue, { qty: item.qty })
    },
    [saveMealItem]
  )

  const handleMealItemNameBlur = useCallback(
    (item, mealIdValue, planId) => {
      if (!item?.name || !String(item.name).trim()) return
      setMealItemSuggestions((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      setMealItemSearchTerms((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      saveMealItem(item, mealIdValue, { name: item.name })
    },
    [saveMealItem]
  )

  const handleMealItemFieldChange = useCallback(
    (item, mealIdValue, planId, field, value) => {
      updateMealItemLocal(item.id, mealIdValue, (food) => ({ ...food, [field]: value }))
    },
    [updateMealItemLocal]
  )

  const handleMealItemFieldBlur = useCallback(
    (item, mealIdValue, planId, field) => {
      saveMealItem(item, mealIdValue, { [field]: item[field] })
    },
    [saveMealItem]
  )

  const handleAddBlankMealItem = useCallback((meal, plan) => {
    if (!meal || !plan) return
    const tempId = `new-${meal.id}-${Date.now()}`
    const blankItem = {
      id: tempId,
      mealfoodId: null,
      mealId: meal.apiId || meal.id,
      name: '',
      qty: 1,
      unit: '',
      calories: 0,
      protein: 0,
      carbs: 0,
      fat: 0,
      note: '',
      micronutrients: {},
    }
    setNutritionPlans((prev) =>
      prev.map((entry) => {
        if (entry.id !== plan.id) return entry
        return {
          ...entry,
          meals: (entry.meals || []).map((m) =>
            m.id === meal.id ? { ...m, items: [...(m.items || []), blankItem] } : m
          ),
        }
      })
    )
  }, [])

  const handleMealItemCopy = useCallback(
    async (item, mealIdValue) => {
      if (!companyUrl) {
        setError((prev) => prev || 'Missing company context.')
        return
      }
      if (!item?.mealfoodId) {
        setError((prev) => prev || 'Unable to copy this item.')
        return
      }
      const resolvedMealId = (() => {
        const raw = mealIdValue ?? item.mealId
        const numeric = Number(raw)
        return Number.isFinite(numeric) ? numeric : raw
      })()
      if (!resolvedMealId) {
        setError((prev) => prev || 'Missing meal id for this item.')
        return
      }
      setMealItemCopyLoading((prev) => ({ ...prev, [item.id]: true }))
      try {
        const res = await apiService.post(`/${companyUrl}/copymealfood`, {
          from_meal_id: resolvedMealId,
          food_id: item.mealfoodId,
        })
        if (!res.success) {
          setError((prev) => prev || res.error || 'Unable to copy food item.')
          return
        }
        await refreshMealPlans()
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          setError((prev) => prev || 'Unable to copy food item.')
        }
      } finally {
        setMealItemCopyLoading((prev) => {
          const next = { ...prev }
          delete next[item.id]
          return next
        })
      }
    },
    [companyUrl, refreshMealPlans]
  )

  const handleMealItemSuggestionSelect = useCallback(
    async (item, mealIdValue, planId, suggestion) => {
      if (!companyUrl) {
        setError((prev) => prev || 'Missing company context.')
        return
      }
      // Branded items are looked up by their Nutritionix id; common foods go through /nutrition/nutrients
      // (for common foods `resource_id` is just a tag id, so it must not be used as a barcode)
      const scanId = suggestion?.nix_item_id
      const foodName = suggestion?.item_name || suggestion?.name || ''
      let detail = null
      try {
        const res = scanId
          ? await apiService.searchNutritionByScan(companyUrl, scanId)
          : await apiService.fetchNutritionNutrients(companyUrl, foodName)
        if (res.success) {
          detail = Array.isArray(res.data?.foods) ? res.data.foods[0] : null
        }
        if (!detail) {
          setError((prev) => prev || res.error || `No nutrition data found for ${foodName}`)
        }
      } catch (err) {
        if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
          setError((prev) => prev || 'Unable to fetch nutrition data')
        }
      }
      const nextValues = (() => {
        if (!detail) {
          return {
            name: suggestion?.item_name || suggestion?.name || item.name,
          }
        }
        return {
          name: detail.name || detail.food_name || suggestion?.item_name || suggestion?.name || item.name,
          qty: detail.qty ?? detail.serving_qty ?? item.qty,
          unit: detail.unit || detail.serving_unit || detail.serving_uom || item.unit,
          calories: toNumberOrZero(detail.calories ?? detail.nf_calories),
          protein: toNumberOrZero(detail.protein ?? detail.nf_protein),
          carbs: toNumberOrZero(detail.carbs ?? detail.nf_total_carbohydrate),
          fat: toNumberOrZero(detail.fat ?? detail.nf_total_fat),
          micronutrients: extractMicronutrients(detail),
        }
      })()
      updateMealItemLocal(item.id, mealIdValue, (food) => ({ ...food, ...nextValues }))
      setMealItemSearchTerms((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      setMealItemSuggestions((prev) => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      await saveMealItem(item, mealIdValue, { ...nextValues, foodDetail: detail })
    },
    [companyUrl, saveMealItem, updateMealItemLocal]
  )

  const handleMealPlanDayToggle = useCallback(
    async (plan, day, nextValue) => {
      if (!plan || !day) return
      const dayField = dayFieldMap[day]
      if (!dayField) return
      if (!companyUrl) {
        setError((prev) => prev || 'Missing company context.')
        return
      }
      const planId = plan.apiId || plan.id
      if (!planId) {
        setError((prev) => prev || 'Unable to update meal plan.')
        return
      }
      const programId =
        plan.programId ||
        programKey ||
        location.state?.programId ||
        location.state?.session?.program_id ||
        location.state?.session?.programId ||
        location.state?.session?.sessionProgramId
      if (!programId) {
        setError((prev) => prev || 'Missing program context.')
        return
      }
      const previousDays = Array.isArray(plan.days) ? plan.days : []
      const nextDays = nextValue
        ? dayOrder.filter((label) => label === day || previousDays.includes(label))
        : previousDays.filter((label) => label !== day)
      const saveKey = `${plan.id}-${day}`
      setMealPlanDaySaving((prev) => ({ ...prev, [saveKey]: true }))
      setNutritionPlans((prev) =>
        prev.map((entry) => (entry.id === plan.id ? { ...entry, days: nextDays } : entry))
      )
      try {
        const payload = { program_id: String(programId), [dayField]: nextValue ? 1 : 0 }
        const res = await apiService.put(`/${companyUrl}/updatemealplan/${planId}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update meal plan.')
        }
      } catch (err) {
        setNutritionPlans((prev) =>
          prev.map((entry) => (entry.id === plan.id ? { ...entry, days: previousDays } : entry))
        )
        setError((prev) => prev || err?.message || 'Unable to update meal plan.')
      } finally {
        setMealPlanDaySaving((prev) => {
          const next = { ...prev }
          delete next[saveKey]
          return next
        })
      }
    },
    [companyUrl, location.state, programKey]
  )

  const resolveProgramId = useCallback(
    (plan) =>
      plan?.programId ||
      programKey ||
      location.state?.programId ||
      location.state?.session?.program_id ||
      location.state?.session?.programId ||
      location.state?.session?.sessionProgramId ||
      sessionId,
    [location.state, programKey, sessionId]
  )

  const handlePlanNameEdit = useCallback((plan) => {
    setPlanNameEditId(plan.id)
    setPlanNameDraft(plan.name || '')
    setPlanNameError('')
  }, [])

  const handlePlanNameCancel = useCallback(() => {
    if (planNameSaving) return
    setPlanNameEditId(null)
    setPlanNameDraft('')
    setPlanNameError('')
  }, [planNameSaving])

  const handlePlanNameSave = useCallback(
    async (plan) => {
      if (!plan) return
      const nextName = planNameDraft.trim()
      if (!nextName) {
        setPlanNameError('Plan name is required.')
        return
      }
      if (!companyUrl) {
        setPlanNameError('Missing company context.')
        return
      }
      const planId = plan.apiId || plan.id
      const programId = resolveProgramId(plan)
      if (!planId || !programId) {
        setPlanNameError('Missing plan context.')
        return
      }
      const previousName = plan.name
      setPlanNameSaving(true)
      setPlanNameError('')
      setNutritionPlans((prev) =>
        prev.map((entry) => (entry.id === plan.id ? { ...entry, name: nextName } : entry))
      )
      try {
        const payload = { program_id: String(programId), name: nextName }
        const res = await apiService.put(`/${companyUrl}/updatemealplan/${planId}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update meal plan name.')
        }
        setPlanNameEditId(null)
      } catch (err) {
        setNutritionPlans((prev) =>
          prev.map((entry) => (entry.id === plan.id ? { ...entry, name: previousName } : entry))
        )
        setPlanNameError(err?.message || 'Unable to update meal plan name.')
      } finally {
        setPlanNameSaving(false)
      }
    },
    [companyUrl, planNameDraft, resolveProgramId]
  )

  const handlePlanDeleteRequest = useCallback((plan) => {
    setPlanDeleteDialog(plan)
  }, [])

  const handlePlanDeleteConfirm = useCallback(async () => {
    if (!planDeleteDialog) return
    if (!companyUrl) {
      setError((prev) => prev || 'Missing company context.')
      return
    }
    const planId = planDeleteDialog.apiId || planDeleteDialog.id
    const programId = resolveProgramId(planDeleteDialog)
    if (!planId || !programId) {
      setError((prev) => prev || 'Unable to delete meal plan.')
      return
    }
    setPlanDeleteLoading(true)
    try {
      const res = await apiService.delete(`/${companyUrl}/deletemealplan/${programId}/${planId}`)
      if (!res.success) {
        throw new Error(res.error || 'Unable to delete meal plan.')
      }
      setPlanDeleteDialog(null)
      await refreshMealPlans()
    } catch (err) {
      setError((prev) => prev || err?.message || 'Unable to delete meal plan.')
    } finally {
      setPlanDeleteLoading(false)
    }
  }, [companyUrl, planDeleteDialog, refreshMealPlans, resolveProgramId])

  const planCopy = useClientProgramCopy({
    companyUrl,
    user,
    defaultClientId: clientId,
    onSaveSuccess: refreshMealPlans,
    validate: (ctx) => {
      if (!ctx.dialog) return 'Missing source plan context.'
      if (!ctx.programId) return 'Select a target program.'
      if (!companyUrl) return 'Missing company context.'
      const fromPlanId = ctx.dialog.apiId || ctx.dialog.id
      const fromProgramId = resolveProgramId(ctx.dialog)
      if (!fromPlanId || !fromProgramId) return 'Missing source plan context.'
      return null
    },
    buildRequests: (ctx) => {
      const fromPlanId = ctx.dialog.apiId || ctx.dialog.id
      const fromProgramId = resolveProgramId(ctx.dialog)
      return {
        endpoint: 'copymealplan',
        payload: {
          from_program_id: String(fromProgramId),
          from_mealplan_id: Number(fromPlanId),
          to_program_id: Number(ctx.programId),
        },
      }
    },
  })

  const nutritionGlobalCopy = useClientProgramCopy({
    companyUrl,
    user,
    defaultClientId: clientId,
    onSaveSuccess: refreshMealPlans,
    validate: (ctx) => {
      if (ctx.targetType === 'different' && !ctx.selectedClient) return 'Select a client.'
      if (!ctx.programId) return 'Select a target program.'
      if (!(nutritionPlans || []).length) return 'No meal plans to copy.'
      if (!Number.isFinite(Number(ctx.programId))) return 'Invalid program selection.'
      return null
    },
    buildRequests: (ctx) => {
      const toProgramId = Number(ctx.programId)
      return (nutritionPlans || [])
        .map((plan) => {
          const fromProgramId = resolveProgramId(plan)
          const fromMealPlanId = plan.apiId || plan.id
          if (!fromProgramId || !fromMealPlanId) return null
          return {
            endpoint: 'copymealplan',
            payload: {
              from_program_id: String(fromProgramId),
              from_mealplan_id: Number(fromMealPlanId),
              to_program_id: toProgramId,
            },
          }
        })
        .filter(Boolean)
    },
  })

  const mealCopy = useClientProgramCopy({
    companyUrl,
    user,
    defaultClientId: clientId,
    onSaveSuccess: refreshMealPlans,
    extraSteps: {
      onProgramSelected: async (programId, { targetClientId }) => {
        if (!companyUrl || !targetClientId || !programId) return []
        const res = await apiService.post(`/${companyUrl}/getsessionplans`, {
          session_id: Number(programId),
          client_id: String(targetClientId),
        })
        if (!res.success) {
          throw new Error(res.error || 'Unable to load meal plans.')
        }
        const plans = res.data?.mealplans || []
        return Array.isArray(plans) ? plans : []
      },
    },
    validate: (ctx) => {
      if (!ctx.dialog) return 'Missing meal context.'
      if (ctx.targetType === 'different' && !ctx.selectedClient) return 'Select a client.'
      if (!companyUrl || !ctx.secondarySelectedId) return 'Select a target meal plan.'
      const fromMealPlanId = ctx.dialog.mealPlanApiId
      const fromMealId = ctx.dialog.meal?.apiId || ctx.dialog.meal?.id
      const toMealPlanId = Number(ctx.secondarySelectedId)
      if (!fromMealPlanId || !fromMealId || !Number.isFinite(toMealPlanId)) return 'Invalid selection.'
      return null
    },
    buildRequests: (ctx) => ({
      endpoint: 'copymealplanmeal',
      payload: {
        to_mealplan_id: Number(ctx.secondarySelectedId),
        from_mealplan_id: Number(ctx.dialog.mealPlanApiId),
        from_meal_id: Number(ctx.dialog.meal?.apiId || ctx.dialog.meal?.id),
      },
    }),
  })

  const handleConfirmMealDelete = async () => {
    if (!mealDeleteDialog) return
    const { mealId, item } = mealDeleteDialog
    if (!companyUrl || !mealId || !item?.id) {
      setMealDeleteDialog(null)
      return
    }
    setMealDeleteLoading(true)
    try {
      const res = await apiService.delete(`/${companyUrl}/deletemealfood/${mealId}/${item.id}`)
      if (!res.success) {
        setError((prev) => prev || res.error || 'Unable to delete food item')
      } else {
        await refreshMealPlans()
      }
    } catch (err) {
      if (err?.name !== 'AbortError' && err?.code !== 'ERR_CANCELED') {
        setError((prev) => prev || 'Unable to delete food item')
      }
    } finally {
      setMealDeleteLoading(false)
      setMealDeleteDialog(null)
    }
  }


  const normalizeMealPlans = useCallback(
    (apiData = []) => {
      const plans = apiData.flatMap((program) => program?.mealplans || [])
      return plans.map((plan, planIndex) => {
        const planApiId = plan.id || plan.mealplan_id || plan.plan_id || plan.mealplanid || null
        const programId = plan.program_id || plan.programId || plan.programid || null
        const meals = Array.isArray(plan.meals) ? [...plan.meals] : []
        const normalizedMeals = meals
          .sort((a, b) => toNumberOrZero(a?.display_order) - toNumberOrZero(b?.display_order))
          .map((meal, mealIndex) => {
            const mealApiId = meal.meal_id || meal.id || meal.guid || null
            const items = Array.isArray(meal.mealfoods) ? [...meal.mealfoods] : []
            const normalizedItems = items
              .sort((a, b) => toNumberOrZero(a?.display_order) - toNumberOrZero(b?.display_order))
              .map((food, foodIndex) => ({
                id: String(food.id || food.mealfood_id || `${mealApiId || meal.id || mealIndex}-food-${foodIndex}`),
                mealfoodId: food.id || food.mealfood_id || null,
                mealId: mealApiId,
                name: food.name || food.cat_name || '—',
                qty: food.qty ?? '',
                unit: food.unit || '',
                calories: toNumberOrZero(food.calories),
                protein: toNumberOrZero(food.protein),
                carbs: toNumberOrZero(food.carbs),
                fat: toNumberOrZero(food.fat),
                note: food.note || food.clientnote || '',
                micronutrients: extractMicronutrients(food),
              }))
            return {
              id: String(meal.id || meal.guid || `${plan.id || planIndex}-meal-${mealIndex}`),
              apiId: mealApiId,
              title: meal.name || 'Meal',
              time: meal.time ? meal.time.slice(0, 5) : '',
              waterCount: toNumberOrZero(meal.water_8oz),
              items: normalizedItems,
            }
          })
        const accent = mealPlanAccents[planIndex % mealPlanAccents.length]
        const days = dayOrder.filter((day) => {
          const field = dayFieldMap[day]
          const value = plan?.[field]
          return value === 1 || value === '1' || value === true
        })
        return {
          id: String(planApiId || plan.name || `plan-${planIndex}`),
          apiId: planApiId,
          programId,
          name: plan.name || 'Meal Plan',
          days,
          accent,
          meals: normalizedMeals,
        }
      })
    },
    []
  )

  const normalizeNutritionTree = useCallback((raw = []) => {
    const root = tryParseJSON(raw)
    const mapNode = (node, index, parentKey = 'root') => {
      const childrenArray =
        node?.children || node?.child || node?.nodes || node?.items || node?.subItems || node?.programnutritiontabtreeindex || []
      const parsedChildren = tryParseJSON(childrenArray)
      const rawId = node?.id || node?.guid || node?.value || node?.name || `node-${index}`
      const stableId = `${parentKey}-${String(rawId)}-${index}`
      const children = Array.isArray(parsedChildren)
        ? parsedChildren.map((child, childIndex) => mapNode(child, childIndex, stableId))
        : []
      return {
        id: stableId,
        sourceId: node?.id || node?.guid || node?.value || null,
        sourceType: node?.sourceType || node?.source_type || node?.type || node?.item_type || '',
        templateId: node?.templateId || node?.template_id || node?.exercisetemplate_id || node?.exercise_id || node?.id || null,
        templateSourceId:
          node?.exerciseplantemplate?.id ||
          node?.exerciseplantemplate_id ||
          node?.exercise_plan_template_id ||
          null,
        isExercisePlanTemplate: Boolean(node?.exerciseplantemplate || node?.exerciseplantemplate_id || node?.exercise_plan_template_id),
        libraryKind: inferNutritionLibraryNodeKind(node, children),
        name: node?.name || node?.title || node?.label || 'Item',
        category: node?.category || node?.catname || node?.cat_name || node?.parent_name || '',
        catid: node?.catid || node?.cat_id || node?.exercisetemplatecategory_id || node?.category_id || node?.parent_id || null,
        catname: node?.catname || node?.cat_name || node?.category || node?.parent_name || '',
        description: node?.description || node?.note || '',
        video_url: node?.video_url || '',
        calories: toNumberOrZero(node?.calories || node?.kcal),
        protein: toNumberOrZero(node?.protein || node?.proteins),
        carbs: toNumberOrZero(node?.carbs || node?.carbohydrates),
        fat: toNumberOrZero(node?.fat || node?.fats),
        qty: node?.qty ?? node?.quantity ?? 1,
        unit: node?.unit || node?.serving || '',
        raw: node && typeof node === 'object' ? node : null,
        children,
      }
    }
    const source = Array.isArray(root)
      ? root
      : root?.programnutritiontabtreeindex || root?.exerciseplancategoryindex || root || []
    const parsedSource = tryParseJSON(source)
    return Array.isArray(parsedSource) ? parsedSource.map(mapNode) : []
  }, [])

  const resolveCategoryName = useCallback(
    (catId) => {
      if (!catId) return ''
      const found = exerciseCategoryOptions.find((opt) => String(opt.id) === String(catId))
      if (found) return found.name
      return ''
    },
    [exerciseCategoryOptions]
  )

  const normalizeExercisePlans = useCallback(
    (sessions = []) => {
      const plans = []
      sessions.forEach((session, sessionIndex) => {
        ;(session.exerciseplans || []).forEach((plan, planIndex) => {
          const planId = String(plan.id || `session-${sessionIndex}-plan-${planIndex}`)
          const exercises = (plan.exerciseplanexercises || [])
            .map((ex, idx) => ({
              id: String(ex.id || `${planId}-ex-${idx}`),
              apiId: ex.id,
              templateId: ex.exercisetemplate_id,
              catId: ex.cat_id,
              catName: ex.cat_name || resolveCategoryName(ex.cat_id),
              name: ex.name || ex.exercisetemplate?.name || 'Exercise',
              sets: ex.sets ?? '',
              reps: ex.reps ?? '',
              rest: ex.rest ?? '',
              speed: ex.speed ?? '',
              mode: ex.mode ?? '',
              intensity: ex.intensity ?? '',
              heartrate: ex.heartrate ?? '',
              duration: ex.duration ?? '',
              note: ex.note ?? '',
              clientnote: ex.clientnote ?? '',
              description: ex.description ?? ex.exercisetemplate?.description ?? '',
              video_url: ex.video_url ?? ex.exercisetemplate?.video_url ?? '',
              order: ex.display_order ?? idx + 1,
              template: ex.exercisetemplate || null,
              isNewRow: false,
            }))
            .sort((a, b) => (a.order || 0) - (b.order || 0))
          const days = dayOrder.filter((day) => {
            const field = dayFieldMap[day]
            const val = plan?.[field]
            return val === 1 || val === '1' || val === true
          })
          plans.push({
            id: planId,
            apiId: plan.id,
            name: plan.name || 'Exercise Plan',
            days,
            exercises,
          })
        })
      })
      return plans
    },
    [resolveCategoryName]
  )

  const countLeaves = useCallback((node) => {
    if (!node?.children?.length) return 1
    return node.children.reduce((acc, child) => acc + countLeaves(child), 0)
  }, [])

  const filterLibraryTree = useCallback((nodes, term) => {
    if (!term) return nodes
    const lowered = term.toLowerCase()
    const filterNode = (node) => {
      const matches = node.name?.toLowerCase().includes(lowered)
      if (!node.children?.length) return matches ? node : null
      const children = node.children.map(filterNode).filter(Boolean)
      if (matches || children.length) return { ...node, children }
      return null
    }
    return nodes.map(filterNode).filter(Boolean)
  }, [])

  const buildLibraryExpansionState = useCallback((nodes, value) => {
    const state = {}
    const walk = (list) => {
      ;(list || []).forEach((node) => {
        state[node.id] = value
        if (node.children?.length) walk(node.children)
      })
    }
    walk(nodes)
    return state
  }, [])

  const [collapsedMeals, setCollapsedMeals] = useState({})

  const resolveProgramValue = () => {
    const sessionMeta = location.state?.session || {}
    return (
      programKey ||
      sessionMeta.sessionProgramId ||
      sessionMeta.program_id ||
      sessionMeta.programId ||
      sessionMeta.programid ||
      sessionMeta.program ||
      sessionId ||
      location.state?.programId
    )
  }

  const refreshMeasurements = useCallback(
    async (customFilter = filterMode, signal = null) => {
      if (!companyUrl) return
      const programValue = resolveProgramValue()
      if (!programValue) return
      setMeasurementsLoading(true)
      setError('')
      try {
        const res = await apiService.post(
          `/${companyUrl}/getsessionmeasurements${customFilter === 'all' ? '' : `?fetchFrom=${customFilter}`}`,
          { program_id: programValue },
          signal
        )
        if (!res.success) {
          if (isNotFoundError(res.error)) {
            const empty = createEmptyMeasurements()
            setMeasurements(empty)
            setEditableCompositions([])
            setEditableCircumferences([])
            setEditableVitals([])
            setOriginalRows({ composition: [], circumference: [], vital: [] })
            return
          }
          setError(res.error || 'Unable to load measurements')
          return
        }
        const meas = res.data || {}
        const comp = Array.isArray(meas.compositions) ? meas.compositions : []
        const circ = Array.isArray(meas.circumferences) ? meas.circumferences : []
        const vit = Array.isArray(meas.vitals) ? meas.vitals : []
        setMeasurements({ compositions: comp, circumferences: circ, vitals: vit })
        setEditableCompositions(comp)
        setEditableCircumferences(circ)
        setEditableVitals(vit)
        setOriginalRows({ composition: comp, circumference: circ, vital: vit })
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to load measurements')
      } finally {
        setMeasurementsLoading(false)
      }
    },
    [companyUrl, filterMode, location.state, programKey, sessionId]
  )

  const handlePrintSubmit = async () => {
    const programValue = resolveProgramValue()
    if (!companyUrl || !programValue || !clientId) {
      setError('Missing program or client information for printing')
      return
    }
    const sectionPath = printSelection === 'all' ? '' : `/${printSelection}`
    const endpoint = `/${companyUrl}/getmeasurementspdf${sectionPath}/${clientId}/${programValue}`
    const token = localStorage.getItem('neat_fit_auth_token')
    try {
      setPrintLoading(true)
      const response = await fetch(`${config.api.baseURL}${endpoint}`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      })
      if (!response.ok) {
        setError('Unable to generate measurements PDF')
        return
      }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setPrintDialogOpen(false)
    } catch (err) {
      if (err?.name === 'AbortError') return
      setError('Unable to generate measurements PDF')
    } finally {
      setPrintLoading(false)
    }
  }

  const handleFieldChange = (type, rowKey, field, value) => {
    const updateList = (list) =>
      list.map((row, index) => {
        const key = getRowKey(row, type, index)
        if (key !== rowKey) return row
        return { ...row, [field]: value }
      })

    if (type === 'composition') setEditableCompositions((prev) => updateList(prev))
    if (type === 'circumference') setEditableCircumferences((prev) => updateList(prev))
    if (type === 'vital') setEditableVitals((prev) => updateList(prev))
  }

  const handleEditRow = (type, rowKey) => {
    setEditingRow({ type, id: rowKey })
  }

  const handleCancelEdit = (type, rowKey) => {
    const revertList = (list, originals) =>
      list.map((row, index) => {
        const key = getRowKey(row, type, index)
        if (key !== rowKey) return row
        const original = originals.find((item, originalIndex) => getRowKey(item, type, originalIndex) === rowKey)
        return original || row
      })

    if (type === 'composition') {
      const isNew = String(rowKey).startsWith('new-')
      if (isNew) setEditableCompositions((prev) => prev.filter((row, index) => getRowKey(row, type, index) !== rowKey))
      else setEditableCompositions((prev) => revertList(prev, originalRows.composition || []))
    }
    if (type === 'circumference') {
      const isNew = String(rowKey).startsWith('new-')
      if (isNew) setEditableCircumferences((prev) => prev.filter((row, index) => getRowKey(row, type, index) !== rowKey))
      else setEditableCircumferences((prev) => revertList(prev, originalRows.circumference || []))
    }
    if (type === 'vital') {
      const isNew = String(rowKey).startsWith('new-')
      if (isNew) setEditableVitals((prev) => prev.filter((row, index) => getRowKey(row, type, index) !== rowKey))
      else setEditableVitals((prev) => revertList(prev, originalRows.vital || []))
    }
    setEditingRow(null)
  }

  const handleSaveRow = async (type, rowKey) => {
    if (type !== 'composition' && type !== 'circumference' && type !== 'vital') {
      setEditingRow(null)
      return
    }
    if (type === 'composition') {
      const targetIndex = editableCompositions.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      if (targetIndex === -1) return
      const row = editableCompositions[targetIndex]
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      if (!companyUrl || !programId) {
        setError('Missing program information for composition')
        return
      }
      const payload = {
        measured_at: formatForApiDate(row.measured_at),
        program_id: programId,
        weight: parseNumber(row.weight),
        bodyfat: parseNumber(row.bodyfat),
        leanmuscle: parseNumber(row.leanmuscle),
        waterweight: parseNumber(row.waterweight),
        fatmass: parseNumber(row.fatmass),
        active: true,
        startDate: toIsoDateString(row.measured_at || new Date()),
      }
      if (String(rowKey).startsWith('new-') || !getWeightId(row)) payload.edit = false
      try {
        setActionLoading(true)
        const res = await apiService.post(`/${companyUrl}/createcomposition`, payload)
        if (!res.success) {
          setError(res.error || 'Unable to save composition')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to save composition')
      } finally {
        setActionLoading(false)
      }
      return
    }
    if (type === 'circumference') {
      const targetIndex = editableCircumferences.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      if (targetIndex === -1) return
      const row = editableCircumferences[targetIndex]
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      if (!companyUrl || !programId) {
        setError('Missing program information for circumference')
        return
      }
      const isNew = String(rowKey).startsWith('new-') || !getCircumferenceId(row)
      const payload = {
        measured_at: formatForApiDate(row.measured_at),
        program_id: programId,
        neck: parseNumber(row.neck),
        arm: parseNumber(row.arm),
        chest: parseNumber(row.chest),
        waist: parseNumber(row.waist),
        hip: parseNumber(row.hip),
        thigh: parseNumber(row.thigh),
        calf: parseNumber(row.calf),
        forearm: parseNumber(row.forearm),
        active: true,
        startDate: toIsoDateString(row.measured_at || new Date()),
      }
      if (!isNew) payload.id = getCircumferenceId(row)
      if (isNew) payload.edit = false
      const creatorId = user?.id || user?.userId
      if (creatorId) payload.created_by = creatorId
      try {
        setActionLoading(true)
        const res = await apiService.post(`/${companyUrl}/createcircumference`, payload)
        if (!res.success) {
          setError(res.error || 'Unable to save circumference')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to save circumference')
      } finally {
        setActionLoading(false)
      }
      return
    }
    if (type === 'vital') {
      const targetIndex = editableVitals.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      if (targetIndex === -1) return
      const row = editableVitals[targetIndex]
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      if (!companyUrl || !programId) {
        setError('Missing program information for vitals')
        return
      }
      const isNew = String(rowKey).startsWith('new-') || !getVitalId(row)
      const payload = {
        measured_at: formatForApiDate(row.measured_at),
        program_id: programId,
        resting_heart_rate: parseNumber(row.resting_heart_rate),
        active_heart_rate: parseNumber(row.active_heart_rate),
        resting_blood_pressure: parseNumber(row.resting_blood_pressure),
        active_blood_pressure: parseNumber(row.active_blood_pressure),
        o2_saturation: parseNumber(row.o2_saturation),
        body_temperature: parseNumber(row.body_temperature),
        vo2_max: parseNumber(row.vo2_max),
        active: true,
        startDate: toIsoDateString(row.measured_at || new Date()),
      }
      if (!isNew) payload.id = getVitalId(row)
      if (isNew) payload.edit = false
      const creatorId = user?.id || user?.userId
      if (creatorId) payload.created_by = creatorId
      try {
        setActionLoading(true)
        const res = await apiService.post(`/${companyUrl}/createvital`, payload)
        if (!res.success) {
          setError(res.error || 'Unable to save vital')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to save vital')
      } finally {
        setActionLoading(false)
      }
    }
  }

  const handleDeleteRow = async (type, rowKey) => {
    if (type === 'vital') {
      const targetIndex = editableVitals.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      const row = targetIndex >= 0 ? editableVitals[targetIndex] : null
      if (!row) return
      setDeleteDialog({ type: 'vital', rowKey, row })
      return
    }
    if (type === 'composition') {
      const targetIndex = editableCompositions.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      const row = targetIndex >= 0 ? editableCompositions[targetIndex] : null
      if (!row) return
      setDeleteDialog({ type: 'composition', rowKey, row })
      return
    }
    if (type === 'circumference') {
      const targetIndex = editableCircumferences.findIndex((row, index) => getRowKey(row, type, index) === rowKey)
      const row = targetIndex >= 0 ? editableCircumferences[targetIndex] : null
      if (!row) return
      setDeleteDialog({ type: 'circumference', rowKey, row })
    }
  }

  const confirmDeleteComposition = async () => {
    if (!deleteDialog) return
    const { type, rowKey, row } = deleteDialog
    if (type === 'composition') {
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      const weightId = getWeightId(row)
      if (!programId || !weightId) {
        setEditableCompositions((prev) => prev.filter((item, index) => getRowKey(item, 'composition', index) !== rowKey))
        setEditingRow((prev) => (prev?.type === 'composition' && prev?.id === rowKey ? null : prev))
        setDeleteDialog(null)
        return
      }
      try {
        setActionLoading(true)
        const res = await apiService.delete(`/${companyUrl}/deleteweight/${programId}/${weightId}`)
        if (!res.success) {
          setError(res.error || 'Unable to delete composition')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to delete composition')
      } finally {
        setActionLoading(false)
        setDeleteDialog(null)
      }
      return
    }
    if (type === 'circumference') {
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      const circId = getCircumferenceId(row)
      if (!programId || !circId) {
        setEditableCircumferences((prev) => prev.filter((item, index) => getRowKey(item, 'circumference', index) !== rowKey))
        setEditingRow((prev) => (prev?.type === 'circumference' && prev?.id === rowKey ? null : prev))
        setDeleteDialog(null)
        return
      }
      try {
        setActionLoading(true)
        const res = await apiService.delete(`/${companyUrl}/deletecircumference/${programId}/${circId}`)
        if (!res.success) {
          setError(res.error || 'Unable to delete circumference')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to delete circumference')
      } finally {
        setActionLoading(false)
        setDeleteDialog(null)
      }
      return
    }
    if (type === 'vital') {
      const programId = getProgramIdForRow(row, programKey || location.state?.programId)
      const vitalId = getVitalId(row)
      if (!programId || !vitalId) {
        setEditableVitals((prev) => prev.filter((item, index) => getRowKey(item, 'vital', index) !== rowKey))
        setEditingRow((prev) => (prev?.type === 'vital' && prev?.id === rowKey ? null : prev))
        setDeleteDialog(null)
        return
      }
      try {
        setActionLoading(true)
        const res = await apiService.delete(`/${companyUrl}/deletevital/${programId}/${vitalId}`)
        if (!res.success) {
          setError(res.error || 'Unable to delete vital')
          return
        }
        await refreshMeasurements()
        setEditingRow(null)
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to delete vital')
      } finally {
        setActionLoading(false)
        setDeleteDialog(null)
      }
    }
  }

  const fetchMeasurements = useCallback(
    async (signal = null) => {
      if (!companyUrl || !clientId || !sessionId) return
      const sessionMeta = location.state?.session || {}
      const programValue =
        sessionMeta.sessionProgramId ||
        sessionMeta.program_id ||
        sessionMeta.programId ||
        sessionMeta.programid ||
        sessionMeta.program ||
        sessionId ||
        location.state?.programId
      const goalValue = location.state?.goalId || sessionMeta.goal_id || location.state?.programId || programValue
      setProgramKey(programValue)
      const runBatch = async (requests) => {
        const results = await Promise.all(
          requests.map(async ({ key, requester, notFoundFallback }) => {
            try {
              const res = await requester()
              if (res.success) return { key, data: res.data }
              const message = res.error || 'Unable to fetch session data'
              if (notFoundFallback !== undefined && isNotFoundError(message)) {
                return { key, data: notFoundFallback }
              }
              return { key, error: message }
            } catch (err) {
              if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') throw err
              return { key, error: 'Unable to fetch session data' }
            }
          })
        )
        return results
      }

      setLoading(true)
      setError('')
      try {
        const batch1 = await runBatch([
          { key: 'nutritionTree', requester: () => apiService.get(`/${companyUrl}/getprogramnutritiontabtree`, {}, signal), notFoundFallback: [] },
          { key: 'staticPages', requester: () => apiService.get(`/${companyUrl}/staticpages`, {}, signal), notFoundFallback: [] },
          {
            key: 'mealPlans',
            requester: () => apiService.post(`/${companyUrl}/getprogrammealplans`, { program_id: programValue, client_id: clientId }, signal),
            notFoundFallback: [],
          },
          {
            key: 'measurements',
            requester: () =>
              apiService.post(
                `/${companyUrl}/getsessionmeasurements${filterMode === 'all' ? '' : `?fetchFrom=${filterMode}`}`,
                { program_id: programValue },
                signal
              ),
            notFoundFallback: createEmptyMeasurements(),
          },
          {
            key: 'sessionExercisePlans',
            requester: () => apiService.post(`/${companyUrl}/exercise/getsessionexerciseplans`, { session_id: sessionId, client_id: clientId }, signal),
            notFoundFallback: [],
          },
          { key: 'exerciseCategoryTree', requester: () => apiService.get(`/${companyUrl}/exercise/getexercisecategorytabtree`, {}, signal), notFoundFallback: [] },
          { key: 'exerciseTemplateCategories', requester: () => apiService.get(`/${companyUrl}/exercise/getexercisetemplatecategories`, {}, signal), notFoundFallback: [] },
        ])
        const batch2 = await runBatch([
          { key: 'goalSessions', requester: () => apiService.post(`/${companyUrl}/getgoalsessions`, { goal_id: goalValue }, signal), notFoundFallback: [] },
          { key: 'sessionPlans', requester: () => apiService.post(`/${companyUrl}/getsessionplans`, { session_id: sessionId, client_id: clientId }, signal), notFoundFallback: [] },
        ])
        const combined = [...batch1, ...batch2]
        const nextDetail = {}
        combined.forEach((item) => {
          if (item.error) setError((prev) => prev || item.error)
          else nextDetail[item.key] = item.data
        })
        setDetailData(nextDetail)
        const meas = nextDetail.measurements || {}
        const comp = Array.isArray(meas.compositions) ? meas.compositions : []
        const circ = Array.isArray(meas.circumferences) ? meas.circumferences : []
        const vit = Array.isArray(meas.vitals) ? meas.vitals : []
        setMeasurements({
          compositions: comp,
          circumferences: circ,
          vitals: vit,
        })
        setEditableCompositions(comp)
        setEditableCircumferences(circ)
        setEditableVitals(vit)
        setOriginalRows({ composition: comp, circumference: circ, vital: vit })
      } catch (err) {
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        setError('Unable to load session data')
      } finally {
        setLoading(false)
      }
    },
    [clientId, companyUrl, location.state, sessionId]
  )

  useEffect(() => {
    const controller = new AbortController()
    fetchMeasurements(controller.signal)
    return () => controller.abort()
  }, [fetchMeasurements])

  useEffect(() => {
    if (!programKey) return undefined
    const controller = new AbortController()
    refreshMeasurements(filterMode, controller.signal)
    return () => controller.abort()
  }, [filterMode, programKey, refreshMeasurements])

  useEffect(() => {
    if (!detailData?.mealPlans) {
      setNutritionPlans([])
      return
    }
    const normalized = normalizeMealPlans(Array.isArray(detailData.mealPlans) ? detailData.mealPlans : [])
    setNutritionPlans(normalized)
    if (normalized.length && !normalized.some((plan) => plan.id === activeNutritionPlanId)) {
      setActiveNutritionPlanId(normalized[0].id)
    }
    const nextWater = normalized.reduce((acc, plan) => {
      plan.meals.forEach((meal) => {
        acc[meal.id] = meal.waterCount || 0
      })
      return acc
    }, {})
    setMealWaterCounts((prev) => ({ ...nextWater, ...prev }))
  }, [activeNutritionPlanId, detailData?.mealPlans, normalizeMealPlans])

  useEffect(() => {
    if (!detailData?.nutritionTree) {
      setLibraryTree([])
      setLibraryExpanded({})
      return
    }
    const raw =
      detailData.nutritionTree?.programnutritiontabtreeindex ||
      detailData.nutritionTree?.exerciseplancategoryindex ||
      detailData.nutritionTree
    const normalized = normalizeNutritionTree(raw)
    const cleaned = (() => {
      if (Array.isArray(normalized) && normalized.length === 1 && normalized[0]?.name?.toLowerCase() === 'item' && normalized[0]?.children?.length) {
        return normalized[0].children
      }
      return Array.isArray(normalized)
        ? normalized.filter((node) => !(node?.name?.toLowerCase() === 'item' && node?.children?.length))
        : []
    })()
    setLibraryTree(cleaned)
    if (Array.isArray(cleaned) && cleaned.length) {
      setLibraryExpanded((prev) => {
        const next = buildLibraryExpansionState(cleaned, false)
        if (!Object.keys(prev).length) return next
        let changed = false
        const merged = { ...prev }
        Object.keys(next).forEach((key) => {
          if (merged[key] === undefined) {
            merged[key] = false
            changed = true
          }
        })
        return changed ? merged : prev
      })
    }
  }, [buildLibraryExpansionState, detailData?.nutritionTree, normalizeNutritionTree])

  useEffect(() => {
    const rawCategories = Array.isArray(detailData?.exerciseTemplateCategories) ? detailData.exerciseTemplateCategories : []
    if (!rawCategories.length) {
      setExerciseCategoryOptions([])
      return
    }
    const options = []
    rawCategories.forEach((cat) => {
      if (Array.isArray(cat.childs) && cat.childs.length) {
        cat.childs.forEach((child) => {
          options.push({ id: child.id, name: child.name, parentId: cat.id, parentName: cat.name })
        })
      } else {
        options.push({ id: cat.id, name: cat.name, parentId: cat.parent_id || null, parentName: cat.parent_name || '' })
      }
    })
    setExerciseCategoryOptions(options)
  }, [detailData?.exerciseTemplateCategories])

  useEffect(() => {
    const normalized = normalizeExercisePlans(Array.isArray(detailData?.sessionExercisePlans) ? detailData.sessionExercisePlans : [])
    setExercisePlans(normalized)
    if (normalized.length && !normalized.some((plan) => plan.id === activeExercisePlanId)) {
      setActiveExercisePlanId(normalized[0].id)
    }
  }, [activeExercisePlanId, detailData?.sessionExercisePlans, normalizeExercisePlans])

  useEffect(() => {
    exercisePlansRef.current = exercisePlans
  }, [exercisePlans])

  useEffect(() => {
    const normalized = normalizeNutritionTree(detailData?.exerciseCategoryTree || [])
    const cleaned = Array.isArray(normalized) ? normalized : []
    setExerciseLibraryTree(cleaned)
    if (cleaned.length && !Object.keys(exerciseLibraryExpanded).length) {
      setExerciseLibraryExpanded(buildLibraryExpansionState(cleaned, false))
    }
  }, [buildLibraryExpansionState, detailData?.exerciseCategoryTree, exerciseLibraryExpanded, normalizeNutritionTree])

  useEffect(() => {
    libraryExpandedRef.current = libraryExpanded
  }, [libraryExpanded])

  useEffect(() => {
    exerciseLibraryExpandedRef.current = exerciseLibraryExpanded
  }, [exerciseLibraryExpanded])

  const compositionTotals = useMemo(() => {
    const rows = editableCompositions || []
    return {
      weight: diffField(rows, 'weight'),
      bodyfat: diffField(rows, 'bodyfat'),
      leanmuscle: diffField(rows, 'leanmuscle'),
      waterweight: diffField(rows, 'waterweight'),
      fatmass: diffField(rows, 'fatmass'),
    }
  }, [editableCompositions])

  const circumferenceTotals = useMemo(() => {
    const rows = editableCircumferences || []
    return {
      neck: diffField(rows, 'neck'),
      arm: diffField(rows, 'arm'),
      chest: diffField(rows, 'chest'),
      waist: diffField(rows, 'waist'),
      hip: diffField(rows, 'hip'),
      thigh: diffField(rows, 'thigh'),
      calf: diffField(rows, 'calf'),
      forearm: diffField(rows, 'forearm'),
    }
  }, [editableCircumferences])

  const vitalsTotals = useMemo(() => {
    const rows = editableVitals || []
    return {
      resting_heart_rate: diffField(rows, 'resting_heart_rate'),
      active_heart_rate: diffField(rows, 'active_heart_rate'),
      systolic_blood_pressure: diffField(rows, 'resting_blood_pressure'),
      diastolic_blood_pressure: diffField(rows, 'active_blood_pressure'),
      o2_saturation: diffField(rows, 'o2_saturation'),
      body_temperature: diffField(rows, 'body_temperature'),
      vo2_max: diffField(rows, 'vo2_max'),
    }
  }, [editableVitals])

  const handleCreateMealPlan = useCallback(async () => {
    const name = newPlanName.trim()
    if (!name) {
      setNewPlanError('Plan name is required.')
      return
    }
    if (!companyUrl) {
      setNewPlanError('Missing company context.')
      return
    }
    const programId = resolveProgramId(activeNutritionPlan) || resolveProgramId({})
    if (!programId || !clientId) {
      setNewPlanError('Missing program or client context.')
      return
    }
    setNewPlanSaving(true)
    setNewPlanError('')
    try {
      const payload = { program_id: String(programId), name, client_id: String(clientId) }
      const res = await apiService.post(`/${companyUrl}/createmealplan`, payload)
      if (!res.success) {
        throw new Error(res.error || 'Unable to create meal plan.')
      }
      setNewPlanName('')
      await refreshMealPlans()
    } catch (err) {
      setNewPlanError(err?.message || 'Unable to create meal plan.')
    } finally {
      setNewPlanSaving(false)
    }
  }, [activeNutritionPlan, clientId, companyUrl, newPlanName, refreshMealPlans, resolveProgramId])

  const handleAddMeal = useCallback(async () => {
    if (!companyUrl) {
      setError((prev) => prev || 'Missing company context.')
      return
    }
    const programId = resolveProgramId(activeNutritionPlan) || resolveProgramId({})
    const mealplanId = activeNutritionPlan?.apiId || activeNutritionPlan?.id
    if (!programId || !clientId || !mealplanId) {
      setError((prev) => prev || 'Missing program, client, or meal plan context.')
      return
    }
    setAddMealLoading(true)
    try {
      const payload = {
        program_id: String(programId),
        client_id: String(clientId),
        mealplan_id: Number(mealplanId),
        name: '',
        time: '09:00',
      }
      const res = await apiService.post(`/${companyUrl}/createmeal`, payload)
      if (!res.success) {
        throw new Error(res.error || 'Unable to create meal.')
      }
      await refreshMealPlans()
      await refreshGoalSessions()
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
        }
      }, 150)
    } catch (err) {
      setError((prev) => prev || err?.message || 'Unable to create meal.')
    } finally {
      setAddMealLoading(false)
    }
  }, [activeNutritionPlan, clientId, companyUrl, refreshGoalSessions, refreshMealPlans, resolveProgramId])

  const handleMealMetaUpdate = useCallback(
    async (meal, plan, updates = {}) => {
      if (!meal) return
      const mealIdValue = meal.apiId || meal.id
      const mealplanId = plan?.apiId || plan?.id || meal.mealplan_id || meal.mealplanId
      if (!companyUrl || !mealIdValue || !mealplanId) {
        setError((prev) => prev || 'Missing meal context.')
        return
      }
      const nextName = updates.name !== undefined ? updates.name : meal.title || meal.name || ''
      const nextTimeRaw = updates.time !== undefined ? updates.time : meal.time || ''
      const nextTime = formatTimeForInput(nextTimeRaw)
      const payloadTime = normalizeTimePayload(nextTimeRaw || nextTime)
      const previousValues = { title: meal.title, time: meal.time }
      updateMealHeaderLocal(mealIdValue, (current) => ({ ...current, title: nextName, time: nextTime }))
      setMealHeaderSaving((prev) => ({ ...prev, [mealIdValue]: true }))
      try {
        const payload = {
          mealplan_id: Number(mealplanId) || mealplanId,
          name: nextName,
          time: payloadTime,
        }
        const res = await apiService.put(`/${companyUrl}/updatemeal/${mealIdValue}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update meal.')
        }
      } catch (err) {
        setError((prev) => prev || err?.message || 'Unable to update meal.')
        updateMealHeaderLocal(mealIdValue, (current) => ({ ...current, ...previousValues }))
      } finally {
        setMealHeaderSaving((prev) => {
          const next = { ...prev }
          delete next[mealIdValue]
          return next
        })
      }
    },
    [companyUrl, updateMealHeaderLocal]
  )

  const nutritionTotals = useMemo(() => {
    if (!activeNutritionPlan) return { calories: 0, protein: 0, carbs: 0, fat: 0, micronutrients: {} }
    const allItems = (activeNutritionPlan.meals || []).flatMap((meal) => meal.items || [])
    const macros = allItems.reduce(
      (acc, item) => {
        acc.calories += Number(item.calories) || 0
        acc.protein += Number(item.protein) || 0
        acc.carbs += Number(item.carbs) || 0
        acc.fat += Number(item.fat) || 0
        return acc
      },
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    )
    const micronutrients = aggregateMicronutrients(allItems)
    return { ...macros, micronutrients }
  }, [activeNutritionPlan])

  const openPdfBlob = useCallback((blob, filename = 'document.pdf') => {
    const url = URL.createObjectURL(blob)
    window.open(url, '_blank', 'noopener,noreferrer')
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  }, [])

  const handlePdfRequest = useCallback(
    async (type) => {
      if (!companyUrl) {
        setError((prev) => prev || 'Missing company context.')
        return
      }
      if (type === 'exercises') {
        if (!clientId || !sessionId) {
          setError((prev) => prev || 'Missing session context.')
          return
        }
        setPdfLoading((prev) => ({ ...prev, exercises: true }))
        try {
          const res = await apiService.getExercisesPdf(companyUrl, clientId, sessionId)
          if (!res.success) throw new Error(res.error || 'Unable to fetch exercises PDF.')
          openPdfBlob(res.data, `exercises-${clientId}-${sessionId}.pdf`)
        } catch (err) {
          setError((prev) => prev || err?.message || 'Unable to fetch PDF.')
        } finally {
          setPdfLoading((prev) => ({ ...prev, exercises: false }))
        }
        return
      }
      const programId = resolveProgramId(activeNutritionPlan) || resolveProgramId({})
      if (!programId) {
        setError((prev) => prev || 'Missing program context.')
        return
      }
      if ((type === 'meals' || type === 'shopping') && !clientId) {
        setError((prev) => prev || 'Missing client context.')
        return
      }
      setPdfLoading((prev) => ({ ...prev, [type]: true }))
      try {
        if (type === 'meals') {
          const res = await apiService.getMealsPdf(companyUrl, programId, clientId)
          if (!res.success) throw new Error(res.error || 'Unable to fetch meals PDF.')
          openPdfBlob(res.data, `meals-${clientId}-${programId}.pdf`)
        } else if (type === 'shopping') {
          const res = await apiService.getShoppingListPdf(companyUrl, programId, clientId)
          if (!res.success) throw new Error(res.error || 'Unable to fetch shopping list PDF.')
          openPdfBlob(res.data, `shopping-list-${clientId}-${programId}.pdf`)
        } else if (type === 'recipes') {
          const res = await apiService.getRecipePdf(companyUrl, programId)
          if (!res.success) throw new Error(res.error || 'Unable to fetch recipe PDF.')
          openPdfBlob(res.data, `recipes-${programId}.pdf`)
        }
      } catch (err) {
        setError((prev) => prev || err?.message || 'Unable to fetch PDF.')
      } finally {
        setPdfLoading((prev) => ({ ...prev, [type]: false }))
      }
    },
    [activeNutritionPlan, clientId, companyUrl, openPdfBlob, resolveProgramId, sessionId]
  )

  const filteredLibraryTree = useMemo(
    () => filterLibraryTree(libraryTree, libraryFilter),
    [filterLibraryTree, libraryFilter, libraryTree]
  )
  const filteredExerciseLibraryTree = useMemo(
    () => filterLibraryTree(exerciseLibraryTree, exerciseLibraryFilter),
    [exerciseLibraryFilter, exerciseLibraryTree, filterLibraryTree]
  )



  useEffect(() => {
    const hasSearch = Boolean(String(libraryFilter || '').trim())
    if (hasSearch) {
      if (!librarySearchActiveRef.current) {
        librarySearchSnapshotRef.current = { ...libraryExpandedRef.current }
        librarySearchActiveRef.current = true
      }
      const expandedForResults = buildLibraryExpansionState(filteredLibraryTree, true)
      const keys = Object.keys(expandedForResults)
      if (!keys.length) return
      setLibraryExpanded((prev) => {
        let changed = false
        const next = { ...prev }
        keys.forEach((key) => {
          if (!next[key]) {
            next[key] = true
            changed = true
          }
        })
        return changed ? next : prev
      })
      return
    }

    if (librarySearchActiveRef.current) {
      librarySearchActiveRef.current = false
      const snapshot = librarySearchSnapshotRef.current
      librarySearchSnapshotRef.current = null
      if (snapshot && typeof snapshot === 'object') {
        setLibraryExpanded(snapshot)
      }
    }
  }, [buildLibraryExpansionState, filteredLibraryTree, libraryFilter])

  useEffect(() => {
    const hasSearch = Boolean(String(exerciseLibraryFilter || '').trim())
    if (hasSearch) {
      if (!exerciseLibrarySearchActiveRef.current) {
        exerciseLibrarySearchSnapshotRef.current = { ...exerciseLibraryExpandedRef.current }
        exerciseLibrarySearchActiveRef.current = true
      }
      const expandedForResults = buildLibraryExpansionState(filteredExerciseLibraryTree, true)
      const keys = Object.keys(expandedForResults)
      if (!keys.length) return
      setExerciseLibraryExpanded((prev) => {
        let changed = false
        const next = { ...prev }
        keys.forEach((key) => {
          if (!next[key]) {
            next[key] = true
            changed = true
          }
        })
        return changed ? next : prev
      })
      return
    }

    if (exerciseLibrarySearchActiveRef.current) {
      exerciseLibrarySearchActiveRef.current = false
      const snapshot = exerciseLibrarySearchSnapshotRef.current
      exerciseLibrarySearchSnapshotRef.current = null
      if (snapshot && typeof snapshot === 'object') {
        setExerciseLibraryExpanded(snapshot)
      }
    }
  }, [buildLibraryExpansionState, exerciseLibraryFilter, filteredExerciseLibraryTree])

  const exerciseLibraryLeafItems = useMemo(() => {
    const leaves = []
    const walk = (nodes = []) => {
      nodes.forEach((node) => {
        if (node?.children?.length) walk(node.children)
        else if (node) leaves.push(node)
      })
    }
    walk(exerciseLibraryTree)
    return leaves
  }, [exerciseLibraryTree])
  const substitutionGuideUrl =
    detailData?.staticPages?.substitutionguide_url || detailData?.staticPages?.substitution_guide_url || ''
  const diningOutGuideUrl = detailData?.staticPages?.diningout_url || detailData?.staticPages?.dining_out_url || ''

  const handleOpenStaticPage = useCallback((url) => {
    if (!url) return
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return undefined

    let frameId = null
    let observer = null

    // DashboardLayout's <header> is sticky top-0, h-14 (56px) — the rail's fixed
    // top offset must clear it or the two overlap while scrolling.
    const HEADER_CLEARANCE = 56 + 16

    const syncDesktopRail = () => {
      if (window.innerWidth < 1024) {
        setDesktopRailStyle(null)
        return
      }

      const anchor = sideRailAnchorRef.current
      if (!anchor) return

      const rect = anchor.getBoundingClientRect()
      if (!rect.width) return

      const top = Math.max(rect.top, HEADER_CLEARANCE)

      setDesktopRailStyle({
        top,
        left: rect.left,
        width: rect.width,
        height: Math.max(window.innerHeight - top - 24, 320),
        placeholderHeight: rect.height,
      })
    }

    const queueSync = () => {
      if (frameId) window.cancelAnimationFrame(frameId)
      frameId = window.requestAnimationFrame(syncDesktopRail)
    }

    queueSync()
    window.addEventListener('resize', queueSync)
    window.addEventListener('scroll', queueSync, { passive: true })

    if (typeof ResizeObserver !== 'undefined' && sideRailAnchorRef.current) {
      observer = new ResizeObserver(queueSync)
      observer.observe(sideRailAnchorRef.current)
    }

    return () => {
      window.removeEventListener('resize', queueSync)
      window.removeEventListener('scroll', queueSync)
      if (frameId) window.cancelAnimationFrame(frameId)
      if (observer) observer.disconnect()
    }
  }, [activeTab])


  const handleDragStart = (event) => {
    const type = event.active.data?.current?.type
    if (type === 'libraryItem') setActiveDragNode(event.active.data.current.node)
  }

  const handleDragCancel = () => setActiveDragNode(null)

  const setMealDragError = (mealId, message) => {
    if (!mealId) return
    setMealDragErrors((prev) => ({ ...prev, [mealId]: message }))
  }

  const clearMealDragError = (mealId) => {
    if (!mealId) return
    setMealDragErrors((prev) => {
      if (!(mealId in prev)) return prev
      const next = { ...prev }
      delete next[mealId]
      return next
    })
  }

  const setMealDragLoading = (mealId, isLoading) => {
    if (!mealId) return
    setMealDragLoadingState((prev) => {
      if (isLoading) return { ...prev, [mealId]: true }
      if (!(mealId in prev)) return prev
      const next = { ...prev }
      delete next[mealId]
      return next
    })
  }

  const handleNutritionDragEnd = async (event) => {
    const { active, over } = event
    if (!over) {
      setActiveDragNode(null)
      return
    }
    const activeType = active.data?.current?.type
    const overType = over.data?.current?.type

    // Reorder meal items
    if (activeType === 'mealItem' && overType === 'mealItem') {
      const activeMealId = active.data.current.mealId
      const overMealId = over.data.current.mealId
      if (!activeMealId || activeMealId !== overMealId) return
      clearMealDragError(activeMealId)
      const targetPlanId = active.data.current.planId || over.data.current.planId
      const targetPlan = nutritionPlans.find((plan) => plan.id === targetPlanId)
      const targetMeal = targetPlan?.meals?.find((meal) => meal.id === activeMealId)
      if (!targetMeal) return
      const items = targetMeal.items || []
      const oldIndex = items.findIndex((item) => item.id === active.id)
      const newIndex = items.findIndex((item) => item.id === over.id)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const nextItems = arrayMove(items, oldIndex, newIndex)
      const revertItems = () => {
        setNutritionPlans((prev) =>
          prev.map((plan) => {
            if (plan.id !== targetPlanId) return plan
            return {
              ...plan,
              meals: (plan.meals || []).map((meal) =>
                meal.id === activeMealId ? { ...meal, items } : meal
              ),
            }
          })
        )
      }

      setNutritionPlans((prev) =>
        prev.map((plan) => {
          if (plan.id !== targetPlanId) return plan
          return {
            ...plan,
            meals: (plan.meals || []).map((meal) =>
              meal.id === activeMealId ? { ...meal, items: nextItems } : meal
            ),
          }
        })
      )

      if (!companyUrl) {
        setMealDragError(activeMealId, 'Missing company context.')
        revertItems()
        return
      }
      const mealApiId = targetMeal.apiId
      if (!mealApiId) {
        setMealDragError(activeMealId, 'Unable to reorder items for this meal.')
        revertItems()
        return
      }
      const foods = nextItems.map((item, idx) => ({
        id: item.mealfoodId,
        display_order: idx + 1,
      }))
      if (foods.some((food) => !food.id)) {
        setMealDragError(activeMealId, 'Unable to reorder items until all items are saved.')
        revertItems()
        return
      }
      setMealDragLoading(activeMealId, true)
      try {
        const res = await apiService.put(`/${companyUrl}/reordermealfoods/${mealApiId}`, { foods })
        if (!res.success) {
          throw new Error(res.error || 'Unable to reorder items.')
        }
      } catch (err) {
        setMealDragError(activeMealId, err?.message || 'Unable to reorder items.')
        revertItems()
      } finally {
        setMealDragLoading(activeMealId, false)
      }
      return
    }

    // Drop from library to meal
    if (activeType === 'libraryItem' && (overType === 'mealDrop' || overType === 'mealItem')) {
      const targetMealId = over.data?.current?.mealId
      const targetPlanId = over.data?.current?.planId || activeNutritionPlan?.id
      if (!targetMealId || !targetPlanId) {
        setActiveDragNode(null)
        return
      }
      clearMealDragError(targetMealId)
      const node = active.data?.current?.node
      if (companyUrl && sessionId && clientId) {
        const coerceId = (value) => {
          const num = Number(value)
          return Number.isFinite(num) ? num : value
        }
        const nodeKind = node?.libraryKind || inferNutritionLibraryNodeKind(node, node?.children || [])
        const sourceId = coerceId(node?.sourceId)

        if (nodeKind === 'template') {
          const targetMeal = (activeNutritionPlan?.meals || []).find(
            (m) => String(m.id) === String(targetMealId)
          )
          const displayOrder = targetMeal?.display_order || targetMeal?.displayOrder || 1
          const existingPlanIds = new Set((nutritionPlans || []).map((plan) => String(plan.apiId || plan.id)))

          const templatePayload = {
            id: sourceId,
            session_id: String(sessionId),
            nutritionprogram: String(displayOrder),
            client_id: String(clientId),
          }

          setMealDragLoading(targetMealId, true)
          apiService.post(`/${companyUrl}/addmealtemplatefoodstosession`, templatePayload).then(async (res) => {
            if (!res?.success) {
              console.error('addmealtemplatefoodstosession failed', res?.error || res)
              setMealDragError(targetMealId, res?.error || 'Failed to add meal template.')
            } else {
              const refreshedMealPlans = await refreshMealPlans()
              const normalizedPlans = normalizeMealPlans(Array.isArray(refreshedMealPlans) ? refreshedMealPlans : [])
              const responsePlanId =
                res?.data?.mealplan_id ||
                res?.data?.mealPlanId ||
                res?.data?.mealplanId ||
                res?.data?.id ||
                null
              const responseMatched = normalizedPlans.find(
                (plan) => responsePlanId && String(plan.apiId || plan.id) === String(responsePlanId)
              )
              const newlyAdded =
                responseMatched ||
                normalizedPlans.find((plan) => !existingPlanIds.has(String(plan.apiId || plan.id))) ||
                null
              if (newlyAdded?.id) {
                setActiveNutritionPlanId(newlyAdded.id)
              }
            }
          }).finally(() => setMealDragLoading(targetMealId, false))
        } else if (nodeKind === 'food') {
          const targetPlan = (nutritionPlans || []).find((plan) => String(plan.id) === String(targetPlanId))
          const rawFood = node?.raw && typeof node.raw === 'object' ? node.raw : {}
          // Library items name their macros inconsistently (kcal, proteins,
          // carbohydrates, fats). Send the normalized values the node already
          // computed, or the API stores the food with 0 for every macro.
          const foodPayload = {
            ...rawFood,
            id: sourceId,
            name: rawFood.name || node?.name,
            displayName: rawFood.displayName || rawFood.name || node?.name,
            qty: String(rawFood.qty ?? rawFood.quantity ?? node?.qty ?? 1),
            unit: rawFood.unit || node?.unit || '',
            calories: node?.calories ?? toNumberOrZero(rawFood.calories),
            protein: node?.protein ?? toNumberOrZero(rawFood.protein),
            carbs: node?.carbs ?? toNumberOrZero(rawFood.carbs),
            fat: node?.fat ?? toNumberOrZero(rawFood.fat),
            session_id: String(sessionId),
            client_id: String(clientId),
            meal_id: coerceId(targetMealId),
            mealplan_id: coerceId(targetPlan?.apiId || targetPlan?.id || targetPlanId),
          }

          setMealDragLoading(targetMealId, true)
          apiService.post(`/${companyUrl}/addmealfood`, foodPayload).then(async (res) => {
            if (!res?.success) {
              console.error('addmealfood failed', res?.error || res)
              setMealDragError(targetMealId, res?.error || 'Failed to add food item.')
            } else {
              await refreshMealPlans()
            }
          }).finally(() => setMealDragLoading(targetMealId, false))
        } else {
          const payload = {
            id: sourceId,
            session_id: String(sessionId),
            client_id: String(clientId),
            meal_id: coerceId(targetMealId),
            ...(nodeKind === 'meal' ? { mealtemplate: '1' } : { recipe: '1' }),
          }

          setMealDragLoading(targetMealId, true)
          apiService.post(`/${companyUrl}/addmealtemplatefoodstosession`, payload).then(async (res) => {
            if (!res?.success) {
              console.error('addmealtemplatefoodstosession failed', res?.error || res)
              setMealDragError(targetMealId, res?.error || 'Failed to add nutrition item.')
            } else {
              await refreshMealPlans()
            }
          }).finally(() => setMealDragLoading(targetMealId, false))
        }
      }
    }
    setActiveDragNode(null)
  }

  const adjustMealWaterCount = async (meal, delta) => {
    if (!meal) return
    const mealKey = meal.id
    const previous = mealWaterCounts[mealKey] ?? meal.waterCount ?? 0
    const next = Math.max(0, previous + delta)
    if (next === previous) return
    setMealWaterCounts((prev) => ({ ...prev, [mealKey]: next }))
    if (!companyUrl) {
      setError((prev) => prev || 'Missing company context.')
      setMealWaterCounts((prev) => ({ ...prev, [mealKey]: previous }))
      return
    }
    const planId = activeNutritionPlan?.apiId || activeNutritionPlan?.id
    const mealApiId = meal.apiId || meal.id
    if (!planId || !mealApiId) {
      setError((prev) => prev || 'Unable to update water count.')
      setMealWaterCounts((prev) => ({ ...prev, [mealKey]: previous }))
      return
    }
    try {
      const res = await apiService.put(`/${companyUrl}/updatewaterformeal/${planId}/${mealApiId}`, { water_cnt: next })
      if (!res.success) {
        throw new Error(res.error || 'Unable to update water count.')
      }
    } catch (err) {
      setError((prev) => prev || err?.message || 'Unable to update water count.')
      setMealWaterCounts((prev) => ({ ...prev, [mealKey]: previous }))
    }
  }

  const toggleMealCollapse = (mealId) =>
    setCollapsedMeals((prev) => ({
      ...prev,
      [mealId]: !prev[mealId],
    }))

  const handleExercisePlanDayToggle = useCallback(
    async (plan, day, nextValue) => {
      if (!plan || !day) return
      const dayField = dayFieldMap[day]
      if (!dayField) return
      if (!companyUrl || !sessionId) {
        setError((prev) => prev || 'Missing session or company context.')
        return
      }
      const planApiId = plan.apiId || plan.id
      if (!planApiId) {
        setError((prev) => prev || 'Unable to update workout day.')
        return
      }
      const previousDays = Array.isArray(plan.days) ? plan.days : []
      const nextDays = dayOrder.filter((label) => (label === day ? nextValue : previousDays.includes(label)))
      const saveKey = `${plan.id}-${day}`
      setExerciseDaySaving((prev) => ({ ...prev, [saveKey]: true }))
      setExercisePlans((prev) =>
        prev.map((entry) => (entry.id === plan.id ? { ...entry, days: nextDays } : entry))
      )
      try {
        const weekdayPayload = dayOrder.reduce((acc, label) => {
          const field = dayFieldMap[label]
          acc[field] = nextDays.includes(label) ? 1 : 0
          return acc
        }, {})
        const payload = {
          session_id: String(sessionId),
          name: plan.name || 'Workout',
          ...weekdayPayload,
        }
        const res = await apiService.put(`/${companyUrl}/exercise/updateexerciseplan/${planApiId}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update workout day.')
        }
      } catch (err) {
        setExercisePlans((prev) =>
          prev.map((entry) => (entry.id === plan.id ? { ...entry, days: previousDays } : entry))
        )
        setError((prev) => prev || err?.message || 'Unable to update workout day.')
      } finally {
        setExerciseDaySaving((prev) => {
          const next = { ...prev }
          delete next[saveKey]
          return next
        })
      }
    },
    [companyUrl, sessionId]
  )

  const handleExercisePlanNameDraftChange = useCallback((planId, value) => {
    if (!planId) return
    setExercisePlanNameDrafts((prev) => ({ ...prev, [planId]: value }))
  }, [])

  const handleExercisePlanNameCommit = useCallback(
    async (planId) => {
      if (!planId || !companyUrl || !sessionId) return
      const latestPlan = exercisePlansRef.current.find((plan) => plan.id === planId)
      if (!latestPlan) return
      const currentName = latestPlan.name || 'Workout'
      const draftValue = exercisePlanNameDrafts[planId]
      const nextName = String(draftValue ?? currentName).trim()
      if (!nextName || nextName === currentName) {
        setExercisePlanNameDrafts((prev) => {
          if (!(planId in prev)) return prev
          const next = { ...prev }
          delete next[planId]
          return next
        })
        return
      }
      const planApiId = latestPlan.apiId || latestPlan.id
      if (!planApiId) {
        setError((prev) => prev || 'Unable to update workout name.')
        return
      }
      const previousName = currentName
      setExercisePlanNameSaving((prev) => ({ ...prev, [planId]: true }))
      setExercisePlans((prev) =>
        prev.map((plan) => (plan.id === planId ? { ...plan, name: nextName } : plan))
      )
      try {
        const weekdayPayload = dayOrder.reduce((acc, label) => {
          const field = dayFieldMap[label]
          acc[field] = (latestPlan.days || []).includes(label) ? 1 : 0
          return acc
        }, {})
        const payload = {
          session_id: String(sessionId),
          name: nextName,
          ...weekdayPayload,
        }
        const res = await apiService.put(`/${companyUrl}/exercise/updateexerciseplan/${planApiId}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update workout name.')
        }
      } catch (err) {
        setExercisePlans((prev) =>
          prev.map((plan) => (plan.id === planId ? { ...plan, name: previousName } : plan))
        )
        setError((prev) => prev || err?.message || 'Unable to update workout name.')
      } finally {
        setExercisePlanNameSaving((prev) => {
          const next = { ...prev }
          delete next[planId]
          return next
        })
        setExercisePlanNameDrafts((prev) => {
          if (!(planId in prev)) return prev
          const next = { ...prev }
          delete next[planId]
          return next
        })
      }
    },
    [companyUrl, exercisePlanNameDrafts, sessionId]
  )

  const handleExercisePlanReorder = useCallback(
    async (planId, direction) => {
      if (!companyUrl || !sessionId) return
      const currentPlans = exercisePlansRef.current
      const index = currentPlans.findIndex((plan) => plan.id === planId)
      if (index === -1) return
      const targetIndex = direction === 'up' ? index - 1 : index + 1
      if (targetIndex < 0 || targetIndex >= currentPlans.length) return
      const planA = currentPlans[index]
      const planB = currentPlans[targetIndex]
      const planAApiId = planA.apiId || planA.id
      const planBApiId = planB.apiId || planB.id
      if (!planAApiId || !planBApiId) return
      const orderForA = targetIndex + 1
      const orderForB = index + 1
      const reordered = [...currentPlans]
      reordered[index] = planB
      reordered[targetIndex] = planA
      setExercisePlans(reordered)
      setExercisePlanReorderSaving((prev) => ({ ...prev, [planId]: true }))
      try {
        const res = await apiService.put(`/${companyUrl}/exerciseplans/reorder/${sessionId}`, {
          exerciseplans: [
            { id: Number(planAApiId), display_order: orderForA },
            { id: Number(planBApiId), display_order: orderForB },
          ],
        })
        if (!res.success) {
          throw new Error(res.error || 'Unable to reorder workouts.')
        }
      } catch (err) {
        setExercisePlans(currentPlans)
        setError((prev) => prev || err?.message || 'Unable to reorder workouts.')
      } finally {
        setExercisePlanReorderSaving((prev) => {
          const next = { ...prev }
          delete next[planId]
          return next
        })
      }
    },
    [companyUrl, sessionId]
  )

  const handleExerciseDragStart = (event) => {
    const type = event.active.data?.current?.type
    if (type === 'exerciseLibraryItem') {
      setActiveExerciseDrag(event.active.data.current.node)
      return
    }
    if (type === 'exerciseRow') {
      setActiveExerciseDrag(null)
    }
  }

  const handleExerciseDragCancel = () => {
    setActiveExerciseDrag(null)
  }

  const setPlanDragError = (planId, message) => {
    if (!planId) return
    setPlanDragErrors((prev) => ({ ...prev, [planId]: message }))
  }

  const clearPlanDragError = (planId) => {
    if (!planId) return
    setPlanDragErrors((prev) => {
      if (!(planId in prev)) return prev
      const next = { ...prev }
      delete next[planId]
      return next
    })
  }

  const setPlanDragLoading = (planId, isLoading) => {
    if (!planId) return
    setPlanDragLoadingState((prev) => {
      if (isLoading) return { ...prev, [planId]: true }
      if (!(planId in prev)) return prev
      const next = { ...prev }
      delete next[planId]
      return next
    })
  }

  const handleExerciseDragEnd = async (event) => {
    const { active, over } = event
    setActiveExerciseDrag(null)
    if (!over) return
    const activeType = active.data?.current?.type
    const overType = over.data?.current?.type

    if (activeType === 'exerciseRow' && overType === 'exerciseRow') {
      const activePlanId = active.data?.current?.planId
      const overPlanId = over.data?.current?.planId
      if (!activePlanId || activePlanId !== overPlanId) return
      const targetPlan = exercisePlans.find((plan) => plan.id === activePlanId)
      if (!targetPlan) return
      clearPlanDragError(activePlanId)
      const exercises = targetPlan.exercises || []
      const oldIndex = exercises.findIndex((item) => item.id === active.id)
      const newIndex = exercises.findIndex((item) => item.id === over.id)
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return
      const reordered = arrayMove(exercises, oldIndex, newIndex).map((item, idx) => ({ ...item, order: idx + 1 }))
      const revert = () =>
        setExercisePlans((prev) =>
          prev.map((plan) => (plan.id === activePlanId ? { ...plan, exercises } : plan))
        )
      setExercisePlans((prev) =>
        prev.map((plan) => (plan.id === activePlanId ? { ...plan, exercises: reordered } : plan))
      )

      if (!companyUrl) {
        setPlanDragError(activePlanId, 'Missing company context.')
        revert()
        return
      }
      const planApiId = targetPlan.apiId || targetPlan.id
      if (!planApiId) {
        setPlanDragError(activePlanId, 'Unable to reorder this workout.')
        revert()
        return
      }
      const reorderRows = reordered.map((item, idx) => ({
        id: Number(item.apiId || item.id),
        display_order: idx + 1,
      }))
      if (reorderRows.some((row) => !Number.isFinite(row.id))) {
        setPlanDragError(activePlanId, 'Unable to reorder rows until all exercises are saved.')
        revert()
        return
      }
      setPlanDragLoading(activePlanId, true)
      try {
        const res = await apiService.put(`/${companyUrl}/exercise/reorderexercises/${planApiId}`, {
          exercises: reorderRows,
        })
        if (!res.success) {
          throw new Error(res.error || 'Unable to reorder exercises.')
        }
      } catch (err) {
        setPlanDragError(activePlanId, err?.message || 'Unable to reorder exercises.')
        revert()
      } finally {
        setPlanDragLoading(activePlanId, false)
      }
      return
    }

    if (activeType === 'exerciseLibraryItem' && (overType === 'exerciseDrop' || overType === 'exerciseRow')) {
      const node = active.data?.current?.node || {}
      const planId = over.data?.current?.planId || activeExercisePlanId
      if (!companyUrl || !sessionId || !clientId) {
        setPlanDragError(planId, 'Missing session, client, or company context.')
        return
      }
      clearPlanDragError(planId)
      const catIdValue = Number(node.catid || node.cat_id || node.category_id || node.exercisetemplatecategory_id)
      const templateIdValue = Number(
        node.isExercisePlanTemplate
          ? (node.templateSourceId || node.sourceId || node.templateId || node.id)
          : (node.templateSourceId || node.templateId || node.exercisetemplate_id || node.sourceId || node.id)
      )

      if (node.isExercisePlanTemplate) {
        if (!Number.isFinite(templateIdValue)) {
          setPlanDragError(planId, 'Unable to add this workout template.')
          return
        }
        setPlanDragLoading(planId, true)
        try {
          const payload = {
            id: templateIdValue,
            session_id: String(sessionId),
            client_id: String(clientId),
            catid: Number.isFinite(catIdValue) ? catIdValue : 0,
          }
          const res = await apiService.post(`/${companyUrl}/exercise/addtemplateexercisestosession`, payload)
          if (!res.success) {
            throw new Error(res.error || 'Unable to add workout template.')
          }
          await refreshExercisePlans()
        } catch (err) {
          setPlanDragError(planId, err?.message || 'Unable to add workout template.')
        } finally {
          setPlanDragLoading(planId, false)
        }
        return
      }

      if (!planId) return
      const targetPlan = exercisePlans.find((plan) => plan.id === planId)
      if (!targetPlan) return
      const existingExercises = targetPlan.exercises || []
      const insertIndexRaw = overType === 'exerciseRow'
        ? existingExercises.findIndex((item) => item.id === over.id)
        : existingExercises.length
      const insertIndex = insertIndexRaw < 0 ? existingExercises.length : insertIndexRaw
      const tempExercise = {
        id: `temp-ex-${Date.now()}`,
        apiId: null,
        templateId: node.templateId || node.exercisetemplate_id || node.sourceId || node.id || null,
        catId: node.catid || node.cat_id || '',
        catName: node.catname || node.cat_name || resolveCategoryName(node.catid || node.cat_id) || '',
        name: node.name || 'Exercise',
        sets: '',
        reps: '',
        rest: '',
        speed: '',
        mode: '',
        intensity: '',
        heartrate: '',
        duration: '',
        note: '',
        clientnote: '',
        description: '',
        video_url: '',
        order: insertIndex + 1,
        template: null,
        isNewRow: false,
      }
      const optimistic = [
        ...existingExercises.slice(0, insertIndex),
        tempExercise,
        ...existingExercises.slice(insertIndex),
      ].map((item, idx) => ({ ...item, order: idx + 1 }))
      const revert = () =>
        setExercisePlans((prev) =>
          prev.map((plan) => (plan.id === planId ? { ...plan, exercises: existingExercises } : plan))
        )
      setExercisePlans((prev) =>
        prev.map((plan) => (plan.id === planId ? { ...plan, exercises: optimistic } : plan))
      )

      const planApiId = targetPlan.apiId || targetPlan.id
      const numericPlanId = Number(planApiId)
      if (!Number.isFinite(numericPlanId) || !Number.isFinite(templateIdValue)) {
        setPlanDragError(planId, 'Unable to add this exercise.')
        revert()
        return
      }
      setPlanDragLoading(planId, true)
      try {
        const payload = {
          session_id: String(sessionId),
          client_id: String(clientId),
          exerciseplan_id: numericPlanId,
          catid: Number.isFinite(catIdValue) ? catIdValue : 0,
          name: node.name || 'Exercise',
          isaddedmanual: true,
          display_order: insertIndex + 1,
          id: templateIdValue,
        }
        const res = await apiService.post(`/${companyUrl}/exercise/addexercisetoexerciseplan`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to add exercise.')
        }
        await refreshExercisePlans()
      } catch (err) {
        setPlanDragError(planId, err?.message || 'Unable to add exercise.')
        revert()
      } finally {
        setPlanDragLoading(planId, false)
      }
    }
  }

  const handleExerciseFieldChange = useCallback((planId, exerciseId, field, value) => {
    setExercisePlans((prev) =>
      prev.map((plan) => {
        if (plan.id !== planId) return plan
        return {
          ...plan,
          exercises: (plan.exercises || []).map((ex) => {
            if (ex.id !== exerciseId) return ex
            if (field === 'catId') {
              return { ...ex, catId: value, catName: resolveCategoryName(value) }
            }
            return { ...ex, [field]: value }
          }),
        }
      })
    )
  }, [resolveCategoryName])

  const handleExerciseFieldCommit = useCallback(
    async (planId, exerciseId, field, value, previousValue) => {
      if (!companyUrl || !sessionId) return
      const latestPlan = exercisePlansRef.current.find((plan) => plan.id === planId)
      const latestExercise = latestPlan?.exercises?.find((entry) => entry.id === exerciseId)
      const exerciseApiId = latestExercise?.apiId
      const planApiId = latestPlan?.apiId || latestPlan?.id
      if (!exerciseApiId || !planApiId) return
      const saveKey = `${exerciseId}-${field}`
      setExerciseFieldSaving((prev) => ({ ...prev, [saveKey]: true }))
      try {
        const effectiveExercise = { ...(latestExercise || {}), [field]: value }
        const payload = {
          exerciseplan_id: Number(planApiId),
          name: effectiveExercise?.name || '',
          sets: hasMeaningfulValue(effectiveExercise?.sets) ? Number(effectiveExercise.sets) || effectiveExercise.sets : null,
          reps: effectiveExercise?.reps ?? '',
          rest: effectiveExercise?.rest ?? '',
          speed: effectiveExercise?.speed ?? '',
          mode: effectiveExercise?.mode ?? '',
          intensity: hasMeaningfulValue(effectiveExercise?.intensity) ? effectiveExercise.intensity : null,
          heartrate: hasMeaningfulValue(effectiveExercise?.heartrate) ? effectiveExercise.heartrate : null,
          duration: hasMeaningfulValue(effectiveExercise?.duration) ? effectiveExercise.duration : null,
          cat_id: Number(effectiveExercise?.catId) || 0,
          session_id: String(sessionId),
        }
        const res = await apiService.put(`/${companyUrl}/exercise/updateexerciseplanexercise/${exerciseApiId}`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to update exercise.')
        }
      } catch (err) {
        setExercisePlans((prev) =>
          prev.map((plan) => {
            if (plan.id !== planId) return plan
            return {
              ...plan,
              exercises: (plan.exercises || []).map((entry) =>
                entry.id === exerciseId ? { ...entry, [field]: previousValue } : entry
              ),
            }
          })
        )
        setError((prev) => prev || err?.message || 'Unable to update exercise.')
      } finally {
        setExerciseFieldSaving((prev) => {
          const next = { ...prev }
          delete next[saveKey]
          return next
        })
      }
    },
    [companyUrl, sessionId]
  )

  const handleCreateExercisePlan = useCallback(async () => {
    if (!companyUrl || !sessionId) {
      setError((prev) => prev || 'Missing session or company context.')
      return
    }
    setCreateExercisePlanLoading(true)
    try {
      const payload = { session_id: String(sessionId), name: 'New Plan' }
      const res = await apiService.post(`/${companyUrl}/exercise/createexerciseplan`, payload)
      if (!res.success) {
        throw new Error(res.error || 'Unable to create workout.')
      }
      const createdId =
        res.data?.id ||
        res.data?.exerciseplan?.id ||
        res.data?.exercisePlan?.id ||
        res.data?.exerciseplan_id ||
        null
      await refreshExercisePlans()
      if (createdId) {
        setActiveExercisePlanId(String(createdId))
      }
      setTimeout(() => {
        if (typeof window !== 'undefined') {
          window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })
        }
      }, 120)
    } catch (err) {
      setError((prev) => prev || err?.message || 'Unable to create workout.')
    } finally {
      setCreateExercisePlanLoading(false)
    }
  }, [companyUrl, refreshExercisePlans, sessionId])

  const handleExercisePlanDeleteRequest = useCallback((plan) => {
    if (!plan) return
    setExercisePlanDeleteDialog({
      planId: plan.id,
      planApiId: plan.apiId || plan.id,
      name: plan.name || 'Workout',
    })
  }, [])

  const handleExercisePlanDeleteConfirm = useCallback(async () => {
    if (!exercisePlanDeleteDialog) return
    const { planApiId, planId } = exercisePlanDeleteDialog
    if (!companyUrl || !sessionId || !planApiId) {
      setError((prev) => prev || 'Missing context to delete workout.')
      return
    }
    setExercisePlanDeleteLoading(true)
    const previousPlans = exercisePlansRef.current
    setExercisePlans((prev) => prev.filter((plan) => plan.id !== planId))
    try {
      const res = await apiService.delete(`/${companyUrl}/exercise/deleteexerciseplan/${sessionId}/${planApiId}`)
      if (!res.success) {
        throw new Error(res.error || 'Unable to delete workout.')
      }
      setExercisePlanDeleteDialog(null)
      await refreshExercisePlans()
    } catch (err) {
      setExercisePlans(previousPlans)
      setError((prev) => prev || err?.message || 'Unable to delete workout.')
    } finally {
      setExercisePlanDeleteLoading(false)
    }
  }, [companyUrl, exercisePlanDeleteDialog, refreshExercisePlans, sessionId])

  const exercisePlanCopy = useClientProgramCopy({
    companyUrl,
    user,
    defaultClientId: clientId,
    onSaveSuccess: refreshExercisePlans,
    validate: (ctx) => {
      if (!ctx.dialog) return 'Missing workout context.'
      if (ctx.targetType === 'different' && !ctx.selectedClient) return 'Select a client.'
      if (!sessionId || !ctx.programId) return 'Select a target program.'
      const isGlobal = Boolean(ctx.dialog.isGlobal)
      const exercisePlanId = isGlobal ? '' : Number(ctx.dialog.planApiId)
      const toSessionId = Number(ctx.programId)
      if ((!isGlobal && !Number.isFinite(exercisePlanId)) || !Number.isFinite(toSessionId)) {
        return 'Invalid program selection.'
      }
      return null
    },
    buildRequests: (ctx) => {
      const isGlobal = Boolean(ctx.dialog.isGlobal)
      return {
        endpoint: 'exercise/copyandcreateexerciseplan',
        payload: {
          exerciseplan_id: isGlobal ? '' : Number(ctx.dialog.planApiId),
          session_id: String(sessionId),
          to_session_id: Number(ctx.programId),
        },
      }
    },
  })

  const handleExerciseBulkOpen = useCallback((scope, plan = null) => {
    setExerciseBulkDialog({
      scope,
      planId: plan?.id || null,
      planApiId: plan?.apiId || plan?.id || null,
      name: plan?.name || '',
    })
    setExerciseBulkValues({ sets: '', reps: '', rest: '', speed: '' })
    setExerciseBulkError('')
  }, [])

  const handleExerciseBulkClose = useCallback(() => {
    if (exerciseBulkSaving) return
    setExerciseBulkDialog(null)
    setExerciseBulkError('')
  }, [exerciseBulkSaving])

  const handleExerciseBulkSave = useCallback(async () => {
    if (!exerciseBulkDialog) return
    if (!companyUrl || !sessionId || !clientId) {
      setExerciseBulkError('Missing session or client context.')
      return
    }
    const payload = {
      session_id: String(sessionId),
      client_id: String(clientId),
      sets: exerciseBulkValues.sets,
      reps: exerciseBulkValues.reps,
      rest: exerciseBulkValues.rest,
      speed: exerciseBulkValues.speed,
    }
    if (exerciseBulkDialog.scope === 'plan') {
      const planApiId = Number(exerciseBulkDialog.planApiId)
      if (!Number.isFinite(planApiId)) {
        setExerciseBulkError('Missing workout context.')
        return
      }
      payload.exerciseplan_id = planApiId
    }
    setExerciseBulkSaving(true)
    setExerciseBulkError('')
    try {
      const res = await apiService.post(`/${companyUrl}/exercise/copyvaluestoexerciseplan`, payload)
      if (!res.success) {
        throw new Error(res.error || 'Unable to apply values.')
      }
      handleExerciseBulkClose()
      await refreshExercisePlans()
    } catch (err) {
      setExerciseBulkError(err?.message || 'Unable to apply values.')
    } finally {
      setExerciseBulkSaving(false)
    }
  }, [clientId, companyUrl, exerciseBulkDialog, exerciseBulkValues, handleExerciseBulkClose, refreshExercisePlans, sessionId])

  const handleNewExerciseRowCategoryChange = useCallback(
    async (planId, exerciseId, categoryId) => {
      handleExerciseFieldChange(planId, exerciseId, 'catId', categoryId)
      handleExerciseFieldChange(planId, exerciseId, 'templateId', '')
      handleExerciseFieldChange(planId, exerciseId, 'name', '')
      setExerciseTemplateOptionsByRow((prev) => ({ ...prev, [exerciseId]: [] }))
      if (!companyUrl || !Number.isFinite(Number(categoryId))) return
      setExerciseTemplateLoadingByRow((prev) => ({ ...prev, [exerciseId]: true }))
      try {
        const payload = { category_id: Number(categoryId), searchtext: '' }
        const res = await apiService.post(`/${companyUrl}/exercise/searchexercisetemplates`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to load exercise names.')
        }
        const raw =
          (Array.isArray(res.data) && res.data) ||
          res.data?.exercisetemplates ||
          res.data?.exerciseTemplates ||
          res.data?.results ||
          []
        const options = (Array.isArray(raw) ? raw : [])
          .map((entry) => ({
            id: entry?.id || entry?.exercisetemplate_id || entry?.exercise_id || null,
            name: entry?.name || entry?.title || entry?.label || '',
          }))
          .filter((entry) => Number.isFinite(Number(entry.id)) && entry.name)
        setExerciseTemplateOptionsByRow((prev) => ({ ...prev, [exerciseId]: options }))
      } catch (err) {
        setError((prev) => prev || err?.message || 'Unable to load exercise names.')
      } finally {
        setExerciseTemplateLoadingByRow((prev) => {
          const next = { ...prev }
          delete next[exerciseId]
          return next
        })
      }
    },
    [companyUrl, handleExerciseFieldChange]
  )

  const handleNewExerciseRowNameChange = useCallback(
    async (planId, exerciseId, templateId) => {
      const plan = exercisePlansRef.current.find((entry) => entry.id === planId)
      const row = plan?.exercises?.find((entry) => entry.id === exerciseId)
      const rowIndex = Math.max(
        0,
        Number((plan?.exercises || []).findIndex((entry) => entry.id === exerciseId))
      )
      const options = exerciseTemplateOptionsByRow[exerciseId] || []
      const selected = options.find((option) => String(option.id) === String(templateId))
      const selectedName = selected?.name || ''
      handleExerciseFieldChange(planId, exerciseId, 'templateId', templateId)
      handleExerciseFieldChange(planId, exerciseId, 'name', selectedName)
      if (!row || !selectedName || !row.catId) return
      const planApiId = plan?.apiId || plan?.id
      const numericPlanId = Number(planApiId)
      const numericCatId = Number(row.catId)
      const numericTemplateId = Number(templateId)
      if (!companyUrl || !clientId || !sessionId || !Number.isFinite(numericPlanId) || !Number.isFinite(numericCatId)) {
        setError((prev) => prev || 'Missing context to add exercise.')
        return
      }
      setExerciseActionLoading((prev) => ({ ...prev, [exerciseId]: true }))
      try {
        const payload = {
          session_id: String(sessionId),
          client_id: String(clientId),
          exerciseplan_id: numericPlanId,
          catid: numericCatId,
          name: selectedName,
          isaddedmanual: true,
          display_order: rowIndex + 1,
        }
        if (Number.isFinite(numericTemplateId)) {
          payload.id = numericTemplateId
        }
        const res = await apiService.post(`/${companyUrl}/exercise/addexercisetoexerciseplan`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to add exercise.')
        }
        await refreshExercisePlans()
      } catch (err) {
        setError((prev) => prev || err?.message || 'Unable to add exercise.')
      } finally {
        setExerciseActionLoading((prev) => {
          const next = { ...prev }
          delete next[exerciseId]
          return next
        })
        setExerciseTemplateOptionsByRow((prev) => {
          const next = { ...prev }
          delete next[exerciseId]
          return next
        })
      }
    },
    [clientId, companyUrl, exerciseTemplateOptionsByRow, handleExerciseFieldChange, refreshExercisePlans, sessionId]
  )

  const handleAddExerciseRow = (plan) => {
    if (!plan) return
    const newExercise = {
      id: `temp-ex-${Date.now()}`,
      apiId: null,
      templateId: null,
      catId: '',
      catName: '',
      name: '',
      sets: '',
      reps: '',
      rest: '',
      speed: '',
      mode: '',
      intensity: '',
      heartrate: '',
      duration: '',
      note: '',
      clientnote: '',
      description: '',
      video_url: '',
      order: (plan.exercises?.length || 0) + 1,
      template: null,
      isNewRow: true,
    }
    setExercisePlans((prev) =>
      prev.map((p) => (p.id === plan.id ? { ...p, exercises: [...(p.exercises || []), newExercise] } : p))
    )
  }

  const handleExerciseDeleteRequest = useCallback((planId, planApiId, planName, exercise) => {
    if (!planId || !exercise) return
    setExerciseDeleteDialog({
      planId,
      planApiId: planApiId || planId,
      planName: planName || 'Workout',
      exerciseId: exercise.id,
      exerciseApiId: exercise.apiId || null,
      exerciseName: exercise.name || 'Exercise',
    })
  }, [])

  const handleConfirmExerciseDelete = useCallback(async () => {
    if (!exerciseDeleteDialog) return
    const { planId, planApiId, exerciseId, exerciseApiId } = exerciseDeleteDialog
    if (!planId || !exerciseId) return
    const currentPlan = exercisePlans.find((plan) => plan.id === planId)
    const previousExercises = currentPlan?.exercises || []
    if (!exerciseApiId) {
      setExercisePlans((prev) =>
        prev.map((plan) =>
          plan.id === planId
            ? { ...plan, exercises: (plan.exercises || []).filter((exercise) => exercise.id !== exerciseId) }
            : plan
        )
      )
      setExerciseTemplateOptionsByRow((prev) => {
        const next = { ...prev }
        delete next[exerciseId]
        return next
      })
      setExerciseTemplateLoadingByRow((prev) => {
        const next = { ...prev }
        delete next[exerciseId]
        return next
      })
      setExerciseActionLoading((prev) => {
        const next = { ...prev }
        delete next[exerciseId]
        return next
      })
      setExerciseDeleteDialog(null)
      return
    }
    if (!companyUrl || !planApiId) {
      setError((prev) => prev || 'Missing context to delete this exercise.')
      return
    }
    setExerciseDeleteLoading(true)
    setExercisePlans((prev) =>
      prev.map((plan) => {
        if (plan.id !== planId) return plan
        return {
          ...plan,
          exercises: (plan.exercises || []).filter((exercise) => exercise.id !== exerciseId),
        }
      })
    )
    try {
      const res = await apiService.delete(`/${companyUrl}/exercise/deleteexerciseplanexercise/${planApiId}/${exerciseApiId}`)
      if (!res.success) {
        throw new Error(res.error || 'Unable to delete exercise.')
      }
      setExerciseDeleteDialog(null)
      await refreshExercisePlans()
    } catch (err) {
      setExercisePlans((prev) =>
        prev.map((plan) => (plan.id === planId ? { ...plan, exercises: previousExercises } : plan))
      )
      setError((prev) => prev || err?.message || 'Unable to delete exercise.')
    } finally {
      setExerciseDeleteLoading(false)
    }
  }, [companyUrl, exerciseDeleteDialog, exercisePlans, refreshExercisePlans])

  const handleExerciseCopy = useCallback(
    async (planId, exercise) => {
      if (!exercise || !planId) return
      const plan = exercisePlansRef.current.find((entry) => entry.id === planId)
      const planApiId = plan?.apiId || plan?.id
      const sourceExerciseId = exercise.apiId || exercise.id
      const numericPlanId = Number(planApiId)
      const numericSourceId = Number(sourceExerciseId)
      if (!companyUrl || !Number.isFinite(numericPlanId) || !Number.isFinite(numericSourceId)) {
        setError((prev) => prev || 'Unable to copy this exercise.')
        return
      }
      const loadingKey = `copy-${planId}-${exercise.id}`
      setExerciseActionLoading((prev) => ({ ...prev, [loadingKey]: true }))
      const previousExercises = plan?.exercises || []
      const sourceIndex = previousExercises.findIndex((entry) => entry.id === exercise.id)
      const insertIndex = sourceIndex === -1 ? previousExercises.length : sourceIndex + 1
      const optimisticCopy = {
        ...exercise,
        id: `temp-copy-${exercise.id}-${Date.now()}`,
        apiId: null,
        order: insertIndex + 1,
      }
      const optimisticExercises = [
        ...previousExercises.slice(0, insertIndex),
        optimisticCopy,
        ...previousExercises.slice(insertIndex),
      ].map((entry, idx) => ({ ...entry, order: idx + 1 }))
      setExercisePlans((prev) =>
        prev.map((entry) => (entry.id === planId ? { ...entry, exercises: optimisticExercises } : entry))
      )
      try {
        const payload = {
          exerciseplan_id: numericPlanId,
          exerciseplanexercise_id: numericSourceId,
        }
        const res = await apiService.post(`/${companyUrl}/exercise/copyandcreateexerciseplanexercise`, payload)
        if (!res.success) {
          throw new Error(res.error || 'Unable to copy exercise.')
        }
        await refreshExercisePlans()
      } catch (err) {
        setExercisePlans((prev) =>
          prev.map((entry) => (entry.id === planId ? { ...entry, exercises: previousExercises } : entry))
        )
        setError((prev) => prev || err?.message || 'Unable to copy exercise.')
      } finally {
        setExerciseActionLoading((prev) => {
          const next = { ...prev }
          delete next[loadingKey]
          return next
        })
      }
    },
    [companyUrl, refreshExercisePlans]
  )

  const handleExerciseInfoOpen = useCallback((exercise) => {
    const text = resolveExerciseInfoText(exercise)
    if (!text) return
    setExerciseInfoDialog({
      name: exercise?.name || 'Exercise',
      description: text,
    })
  }, [])

  const handleExerciseVideoOpen = useCallback((exercise) => {
    const rawUrl = resolveExerciseVideoUrl(exercise)
    if (!rawUrl) return
    setExerciseVideoDialog({
      open: true,
      name: exercise?.name || 'Exercise Video',
      embedUrl: toExerciseEmbedUrl(rawUrl),
      rawUrl,
    })
  }, [])

  const handleExerciseVideoClose = useCallback(() => {
    setExerciseVideoDialog({ open: false, name: '', embedUrl: '', rawUrl: '' })
  }, [])

  const sessionTitle = location.state?.session?.name || `Session ${sessionId}`
  const desktopRailPinned = Boolean(desktopRailStyle)

  const createRowId = () => `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`

  const handleAddRow = (type) => {
    if (type === 'composition') {
      const newRow = {
        id: createRowId(),
        measured_at: new Date().toISOString(),
        weight: null,
        bodyfat: null,
        leanmuscle: null,
        waterweight: null,
        fatmass: null,
      }
      setEditableCompositions((prev) => [...prev, newRow])
      setEditingRow({ type: 'composition', id: newRow.id })
      return
    }
    if (type === 'circumference') {
      const newRow = {
        id: createRowId(),
        measured_at: new Date().toISOString(),
        neck: null,
        arm: null,
        chest: null,
        waist: null,
        hip: null,
        thigh: null,
        calf: null,
        forearm: null,
      }
      setEditableCircumferences((prev) => [...prev, newRow])
      setEditingRow({ type: 'circumference', id: newRow.id })
      return
    }
    if (type === 'vital') {
      const newRow = {
        id: createRowId(),
        measured_at: new Date().toISOString(),
        resting_heart_rate: null,
        active_heart_rate: null,
        resting_blood_pressure: null,
        active_blood_pressure: null,
        o2_saturation: null,
        body_temperature: null,
        vo2_max: null,
      }
      setEditableVitals((prev) => [...prev, newRow])
      setEditingRow({ type: 'vital', id: newRow.id })
    }
  }

  const renderActionCell = (type, rowKey, isEditing) => (
    <td className="px-3 py-3 text-right">
      <div className="flex items-center justify-end gap-2">
        {isEditing && (
          <>
            <button
              type="button"
              onClick={() => handleSaveRow(type, rowKey)}
              disabled={actionLoading || measurementsLoading}
              className="inline-flex items-center gap-1 rounded-md bg-fitness-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow hover:bg-fitness-primary/90 transition disabled:cursor-not-allowed disabled:opacity-70"
            >
              <Save className="h-4 w-4" />
              Save
            </button>
            <button
              type="button"
              onClick={() => handleCancelEdit(type, rowKey)}
              disabled={actionLoading || measurementsLoading}
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-70"
            >
              Cancel
            </button>
          </>
        )}
        {!isEditing && (
          <button
            type="button"
            onClick={() => handleEditRow(type, rowKey)}
            disabled={actionLoading}
            className="rounded-md border border-slate-200 p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={() => handleDeleteRow(type, rowKey)}
          disabled={actionLoading}
          className="rounded-md border border-red-200 p-2 text-red-500 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-70"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </td>
  )

  const renderInput = (type, rowKey, field, value, isDate = false) => {
    if (isDate) {
      return (
        <input
          type="date"
          value={toInputDate(value)}
          onChange={(e) => handleFieldChange(type, rowKey, 'measured_at', e.target.value)}
          className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-800 focus:border-slate-400 focus:outline-none"
        />
      )
    }
    return (
      <input
        type="number"
        value={value ?? ''}
        onChange={(e) => handleFieldChange(type, rowKey, field, e.target.value)}
        className="w-full rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-800 focus:border-slate-400 focus:outline-none"
      />
    )
  }

  return (
    <div className="app-page min-h-screen">
      <div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-2 inline-flex items-center gap-1.5 text-[13px] text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back
        </button>
        <h1 className="text-[1.625rem] font-semibold leading-tight tracking-[-0.025em] text-slate-900">{sessionTitle}</h1>
      </div>

      <div className="w-full space-y-6">
        <UnderlineTabs
          tabs={[
            { id: 'measurements', label: 'Measurements' },
            { id: 'nutrition', label: 'Nutrition' },
            { id: 'exercise', label: 'Exercise' },
          ]}
          value={activeTab}
          onChange={setActiveTab}
          disabled={actionLoading || measurementsLoading}
          label="Session sections"
        />

        {loading && (
          <div className="flex items-center justify-center py-20 text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading session measurements...
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-red-600">{error}</CardContent>
          </Card>
        )}

        {!loading && activeTab === 'measurements' && (
          <div className="relative">
            {measurementsLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                <div className="flex items-center gap-2 text-slate-700">
                  <Loader2 className="h-5 w-5 animate-spin text-fitness-primary" />
                  <span className="text-sm font-semibold">Refreshing measurements...</span>
                </div>
              </div>
            )}
            <div className={`space-y-6 transition ${measurementsLoading ? 'pointer-events-none opacity-60' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-slate-700">Measurements Range</p>
                  <Select value={filterMode} onValueChange={setFilterMode} disabled={loading || actionLoading || measurementsLoading}>
                    <SelectTrigger className="mt-1 w-56">
                      <SelectValue placeholder="All" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="6weeks">Limit to 6 weeks past</SelectItem>
                      <SelectItem value="last8">Show last 8</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPrintSelection('all')
                    setPrintDialogOpen(true)
                  }}
                  disabled={loading || actionLoading || measurementsLoading || printLoading}
                  className="inline-flex items-center gap-2 rounded-md bg-fitness-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-sm transition hover:bg-fitness-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  Print
                </button>
              </div>
              <MeasurementsTable
                type="composition"
                title="Composition"
                unitNote="Measurements are in pounds, percentages in whole numbers."
                columns={COMPOSITION_COLUMNS}
                rows={editableCompositions}
                totals={compositionTotals}
                editingRow={editingRow}
                actionLoading={actionLoading}
                measurementsLoading={measurementsLoading}
                onAddRow={() => handleAddRow('composition')}
                renderInput={renderInput}
                renderActionCell={renderActionCell}
                getRowKey={getRowKey}
              />
              <MeasurementsTable
                type="circumference"
                title="Circumference Measurements"
                unitNote="Measurements are in inches."
                columns={CIRCUMFERENCE_COLUMNS}
                rows={editableCircumferences}
                totals={circumferenceTotals}
                editingRow={editingRow}
                actionLoading={actionLoading}
                measurementsLoading={measurementsLoading}
                onAddRow={() => handleAddRow('circumference')}
                renderInput={renderInput}
                renderActionCell={renderActionCell}
                getRowKey={getRowKey}
              />
              <MeasurementsTable
                type="vital"
                title="Vitals"
                columns={VITALS_COLUMNS}
                rows={editableVitals}
                totals={vitalsTotals}
                editingRow={editingRow}
                actionLoading={actionLoading}
                measurementsLoading={measurementsLoading}
                onAddRow={() => handleAddRow('vital')}
                renderInput={renderInput}
                renderActionCell={renderActionCell}
                getRowKey={getRowKey}
              />
          </div>
        </div>
        )}

        {!loading && activeTab === 'nutrition' && (
          <div className="space-y-4">
            <Card className="rounded-xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-3 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-1.5">
                    <input
                      type="text"
                      placeholder="New plan name"
                      value={newPlanName}
                      onChange={(e) => {
                        setNewPlanName(e.target.value)
                        if (newPlanError) setNewPlanError('')
                      }}
                      className="h-9 w-44 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:border-fitness-primary focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={handleCreateMealPlan}
                      disabled={newPlanSaving}
                      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-slate-900 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <Plus className="h-4 w-4" />
                      {newPlanSaving ? 'Adding...' : 'Add Plan'}
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleOpenStaticPage(substitutionGuideUrl)}
                      disabled={!substitutionGuideUrl}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Substitution Guide
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenStaticPage(diningOutGuideUrl)}
                      disabled={!diningOutGuideUrl}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      Dining Out Guide
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePdfRequest('meals')}
                      disabled={pdfLoading.meals}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Printer className="h-4 w-4" />
                      {pdfLoading.meals ? 'Loading…' : 'Menu'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePdfRequest('shopping')}
                      disabled={pdfLoading.shopping}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Printer className="h-4 w-4" />
                      {pdfLoading.shopping ? 'Loading…' : 'Shopping List'}
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePdfRequest('recipes')}
                      disabled={pdfLoading.recipes}
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Printer className="h-4 w-4" />
                      {pdfLoading.recipes ? 'Loading…' : 'Recipes'}
                    </button>
                    <button
                      type="button"
                      onClick={() => nutritionGlobalCopy.open(true)}
                      disabled={!nutritionPlans.length}
                      title="Copy every meal plan in this program to another program"
                      className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Copy className="h-4 w-4" />
                      Copy complete plan
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    {nutritionPlans.map((plan) => {
                      const isActive = activeNutritionPlan?.id === plan.id
                      const isEditing = planNameEditId === plan.id
                      return (
                        <div
                          key={plan.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => setActiveNutritionPlanId(plan.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') setActiveNutritionPlanId(plan.id)
                          }}
                          className={`group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold shadow-sm ring-1 transition ${
                            isActive
                              ? 'bg-fitness-primary text-primary-foreground ring-fitness-primary/60'
                              : 'bg-white text-slate-700 ring-slate-200 hover:text-fitness-primary'
                          }`}
                        >
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={planNameDraft}
                                onChange={(e) => setPlanNameDraft(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.stopPropagation()
                                    handlePlanNameSave(plan)
                                  }
                                  if (e.key === 'Escape') {
                                    e.stopPropagation()
                                    handlePlanNameCancel()
                                  }
                                }}
                                className="h-8 w-36 rounded-md border border-white/40 bg-white/90 px-2 text-xs text-slate-900"
                              />
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePlanNameSave(plan)
                                }}
                                disabled={planNameSaving}
                                className="rounded-full bg-white/90 p-1 text-fitness-primary shadow-sm transition hover:bg-white"
                              >
                                <Save className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePlanNameCancel()
                                }}
                                disabled={planNameSaving}
                                className="rounded-full bg-white/90 p-1 text-slate-500 shadow-sm transition hover:bg-white"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <span className="truncate max-w-[120px] text-left">{plan.name}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePlanNameEdit(plan)
                                }}
                                className="rounded-full p-0.5 text-slate-400 transition hover:text-fitness-primary"
                              >
                                <Pencil className={`h-3.5 w-3.5 ${isActive ? 'text-white' : ''}`} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  planCopy.open(plan)
                                }}
                                className="rounded-full p-0.5 text-slate-400 transition hover:text-fitness-primary"
                              >
                                <Copy className={`h-3.5 w-3.5 ${isActive ? 'text-white' : ''}`} />
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handlePlanDeleteRequest(plan)
                                }}
                                className="rounded-full p-0.5 text-red-400 transition hover:text-red-500"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>

                {(planNameError || newPlanError) && (
                  <p className="text-sm text-red-600">{planNameError || newPlanError}</p>
                )}

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">Active days</span>
                    {dayOrder.map((day) => {
                      const selected = activeNutritionPlan?.days?.includes(day)
                      const saveKey = activeNutritionPlan ? `${activeNutritionPlan.id}-${day}` : ''
                      const isSaving = saveKey ? Boolean(mealPlanDaySaving[saveKey]) : false
                      return (
                        <label
                          key={day}
                          className={`flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold transition ${
                            selected
                              ? 'border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary'
                              : 'border-slate-200 bg-white text-slate-500 hover:border-fitness-primary/30'
                          } ${isSaving ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={isSaving || !activeNutritionPlan}
                            onChange={(e) => handleMealPlanDayToggle(activeNutritionPlan, day, e.target.checked)}
                            className="h-3 w-3 rounded border-slate-300"
                          />
                          {dayLabels[day]}
                        </label>
                      )
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={handleAddMeal}
                    disabled={addMealLoading || !activeNutritionPlan}
                    className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    {addMealLoading ? 'Adding...' : 'Add Meal'}
                  </button>
                </div>
              </CardContent>
            </Card>

            <DndContext
              sensors={sensors}
              collisionDetection={collisionDetection}
              onDragStart={handleDragStart}
              onDragCancel={handleDragCancel}
              onDragEnd={handleNutritionDragEnd}
            >
              <div className="grid gap-3 lg:items-start lg:grid-cols-[minmax(0,2fr),minmax(0,1fr)] lg:gap-4">
                <div className="order-2 space-y-3 lg:order-1">
                  {(activeNutritionPlan?.meals || []).map((meal) => {
                  const mealTotals = (meal.items || []).reduce(
                    (acc, item) => {
                      const calories = Number(item.calories) || 0
                      const protein = Number(item.protein) || 0
                      const carbs = Number(item.carbs) || 0
                      const fat = Number(item.fat) || 0
                      acc.calories += calories
                      acc.protein += protein
                      acc.carbs += carbs
                      acc.fat += fat
                      return acc
                    },
                    { calories: 0, protein: 0, carbs: 0, fat: 0 }
                  )
                  const waterCount = mealWaterCounts[meal.id] ?? meal.waterCount ?? 0
                  const isCollapsed = collapsedMeals[meal.id]
                  const mealMicronutrients = aggregateMicronutrients(meal.items || [])
                  const mealSaving = mealHeaderSaving[meal.apiId || meal.id]
                  const mealTimeValue = formatTimeForInput(meal.time)
                  const mealNameValue = meal.title || ''
                  const totalCalories = mealTotals.calories || 0
                  const macroPercents = {
                    protein: totalCalories ? Math.round(((mealTotals.protein * 4) / totalCalories) * 100) : 0,
                    carbs: totalCalories ? Math.round(((mealTotals.carbs * 4) / totalCalories) * 100) : 0,
                    fat: totalCalories ? Math.round(((mealTotals.fat * 9) / totalCalories) * 100) : 0,
                  }
                  const micronutrientEntries = micronutrientDisplayOrder
                    .filter((key) => mealMicronutrients[key] !== undefined)
                    .map((key) => ({
                      key,
                      label: micronutrientLabels[key] || key,
                      value: mealMicronutrients[key],
                    }))
                  return (
                    <MealDropZone key={meal.id} mealId={meal.id} planId={activeNutritionPlan?.id}>
                      <Card
                        className={`border-0 shadow-sm ring-1 transition-shadow duration-300 ${
                          mealDragLoading[meal.id]
                            ? 'ring-2 ring-fitness-primary/40 shadow-md'
                            : 'ring-slate-200/70'
                        }`}
                      >
                        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0 px-4 pt-3 pb-3">
                          <div className="flex flex-wrap items-center gap-1">
                            <div className="flex items-center gap-1 rounded-full bg-white ">
                              {/* Free text with suggestions: coaches name meals however they like. */}
                              <input
                                key={`${meal.id}-${mealNameValue}`}
                                type="text"
                                defaultValue={mealNameValue}
                                list={`meal-name-suggestions-${meal.id}`}
                                placeholder="Meal name"
                                aria-label="Meal name"
                                onBlur={(e) => {
                                  const next = e.target.value.trim()
                                  if (next && next !== mealNameValue) handleMealMetaUpdate(meal, activeNutritionPlan, { name: next })
                                }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                                disabled={mealSaving}
                                className="w-36 rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-semibold text-slate-800"
                              />
                              <datalist id={`meal-name-suggestions-${meal.id}`}>
                                {mealNameOptions.map((option) => <option key={option} value={option} />)}
                              </datalist>
                            </div>
                            <div className="flex items-center gap-1 rounded-full bg-white px-3 py-1">
                              {/* <Clock3 className="h-4 w-4 text-slate-500" /> */}
                              <input
                                type="time"
                                value={mealTimeValue}
                                onChange={(e) => handleMealMetaUpdate(meal, activeNutritionPlan, { time: e.target.value })}
                                disabled={mealSaving}
                                className="w-30 rounded-md border border-slate-200 px-2 py-1 text-sm text-slate-800"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                activeNutritionPlan &&
                                mealCopy.open({
                                  meal,
                                  mealPlanApiId: activeNutritionPlan.apiId || activeNutritionPlan.id,
                                  mealPlanName: activeNutritionPlan.name || 'meal plan',
                                })
                              }
                              title="Copy this meal to another meal plan"
                              className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
                            >
                              <Copy className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => toggleMealCollapse(meal.id)}
                              className="rounded-full border border-slate-200 bg-white p-2 text-slate-500 transition hover:border-slate-300 hover:text-slate-800"
                            >
                              {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMealPlanDeleteRequest(meal, activeNutritionPlan)}
                              className="rounded-full border border-red-200 bg-white p-2 text-red-500 transition hover:border-red-300 hover:text-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </CardHeader>
                        {mealDragLoading[meal.id] && (
                          <div className="mx-4 mb-3 overflow-hidden rounded-lg border border-fitness-primary/25 bg-fitness-primary/[0.07]">
                            <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-fitness-primary">
                              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                              <span>Saving changes to this meal…</span>
                            </div>
                            <div className="relative h-1 w-full bg-fitness-primary/15">
                              <div className="animate-indeterminate-bar absolute inset-y-0 rounded-full bg-fitness-primary" />
                            </div>
                          </div>
                        )}
                        {mealDragErrors[meal.id] && (
                          <div className="mx-4 mb-3 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                            <span className="flex-1">{mealDragErrors[meal.id]}</span>
                            <button
                              type="button"
                              onClick={() => clearMealDragError(meal.id)}
                              className="flex-shrink-0 rounded-md p-0.5 text-red-500 transition hover:bg-red-100 hover:text-red-700"
                              aria-label="Dismiss error"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        {!isCollapsed && (
                          <CardContent className="space-y-3 px-4 pt-0 pb-4">
                            <div className="overflow-x-auto rounded-xl border border-slate-200">
                              <table className="w-full min-w-[800px] table-fixed border-collapse text-sm">
                                <thead className="bg-slate-50">
                                  <tr className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    <th style={{ width: '48px' }} className="px-2 py-2.5"></th>
                                    <th className="whitespace-nowrap px-2 py-2.5 text-left">Name</th>
                                    <th style={{ width: '88px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Qty</th>
                                    <th style={{ width: '90px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Unit</th>
                                    <th style={{ width: '64px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Cals</th>
                                    <th style={{ width: '76px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Protein</th>
                                    <th style={{ width: '64px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Carbs</th>
                                    <th style={{ width: '60px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Fat</th>
                                    <th style={{ width: '128px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                  <SortableContext items={(meal.items || []).map((item) => item.id)} strategy={verticalListSortingStrategy}>
                                    {(meal.items || []).map((item) => (
                                      <SortableMealItem
                                        key={item.id}
                                        item={item}
                                        mealId={meal.id}
                                        planId={activeNutritionPlan?.id}
                                        onNote={handleMealNoteOpen}
                                        onNameChange={handleMealItemNameChange}
                                        onNameSelect={handleMealItemSuggestionSelect}
                                        onNameBlur={handleMealItemNameBlur}
                                        nameSuggestions={mealItemSuggestions[item.id] || []}
                                        searchLoading={mealItemSearchLoading[item.id]}
                                        searchTerm={mealItemSearchTerms[item.id]}
                                        onQtyChange={handleMealItemQtyChange}
                                        onQtyBlur={handleMealItemQtyBlur}
                                        onFieldChange={handleMealItemFieldChange}
                                        onFieldBlur={handleMealItemFieldBlur}
                                        onCopy={handleMealItemCopy}
                                        copying={!!mealItemCopyLoading[item.id]}
                                        saving={!!mealItemSaving[item.id]}
                                        onDelete={(targetItem, targetMealId, targetPlanId) =>
                                          handleMealDeleteRequest(targetItem, targetMealId, targetPlanId, meal.title)
                                        }
                                      />
                                    ))}
                                  </SortableContext>
                                  {!(meal.items || []).length && (
                                    <tr>
                                      <td colSpan={9} className="px-3 py-6 text-center text-xs text-slate-400">
                                        No food items yet. Add one below.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleAddBlankMealItem(meal, activeNutritionPlan)}
                              className="inline-flex items-center gap-2 rounded-md border border-fitness-primary bg-white px-3 py-2 text-sm font-semibold text-fitness-primary shadow-sm ring-2 ring-fitness-primary/20 transition hover:bg-fitness-primary/5"
                            >
                              <Plus className="h-4 w-4" />
                              Add food item
                            </button>
                            <div className="flex flex-wrap items-center justify-between gap-4 border-t border-dashed border-slate-200 pt-3">
                              <div className="flex flex-wrap items-center gap-3 text-slate-500">
                                <button
                                  type="button"
                                  onClick={() => adjustMealWaterCount(meal, 1)}
                                  title="Add a glass of water"
                                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-fitness-primary transition hover:border-fitness-primary/40 hover:bg-fitness-primary/5"
                                >
                                  <Droplet className="h-4 w-4" />
                                </button>
                                <div className="flex flex-wrap items-center gap-1">
                                  {Array.from({ length: Math.max(0, waterCount) }).map((_, idx) => (
                                    <button
                                      key={`${meal.id}-water-${idx}`}
                                      type="button"
                                      onClick={() => adjustMealWaterCount(meal, -1)}
                                      title="Remove a glass of water"
                                      className="flex h-6 w-6 items-center justify-center transition hover:scale-110"
                                    >
                                      <img src={waterGlassIcon} alt="Glass of water" className="h-full w-full" />
                                    </button>
                                  ))}
                                </div>
                                <span className="text-xs font-semibold">Water tracker</span>
                              </div>
                              <div className="flex flex-col items-end gap-2 text-sm font-semibold text-slate-800">
                                <div className="flex flex-wrap items-center gap-5">
                                  <span className="flex items-center gap-1">
                                    Cals
                                    <span className="text-slate-500">{Math.round(mealTotals.calories)}</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    Protein
                                    <span className="text-slate-500">{Math.round(mealTotals.protein)}</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    Carbs
                                    <span className="text-slate-500">{Math.round(mealTotals.carbs)}</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    Fat
                                    <span className="text-slate-500">{Math.round(mealTotals.fat)}</span>
                                  </span>
                                  <div
                                    className="relative"
                                    onMouseEnter={() => openMicronutrientPopover(meal.id)}
                                    onMouseLeave={() => scheduleMicronutrientPopoverClose(meal.id)}
                                  >
                                    <button type="button" className="text-xs font-semibold text-fitness-primary hover:text-fitness-primary/80">
                                      Micronutrients
                                    </button>
                                    {activeMicronutrientMeal === meal.id && (
                                      <div
                                        className="absolute right-0 top-full z-20 mt-2 w-56 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white text-sm shadow-lg"
                                        onMouseEnter={() => openMicronutrientPopover(meal.id)}
                                        onMouseLeave={() => scheduleMicronutrientPopoverClose(meal.id)}
                                      >
                                        {micronutrientEntries.length ? (
                                          micronutrientEntries.map((entry) => (
                                            <div
                                              key={`${meal.id}-${entry.key}`}
                                              className="flex items-center justify-between border-b border-slate-100 px-3 py-2 last:border-b-0"
                                            >
                                              <span className="text-slate-700">{entry.label}</span>
                                              <span className="text-slate-500">{Math.round(entry.value)}</span>
                                            </div>
                                          ))
                                        ) : (
                                          <div className="px-3 py-2 text-xs text-slate-500">No micronutrient data</div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-wrap items-center gap-4 text-xs font-semibold text-slate-500">
                                  <span className="flex items-center gap-1">
                                    Protein
                                    <span className="text-slate-700">{macroPercents.protein}%</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    Carbs
                                    <span className="text-slate-700">{macroPercents.carbs}%</span>
                                  </span>
                                  <span className="flex items-center gap-1">
                                    Fat
                                    <span className="text-slate-700">{macroPercents.fat}%</span>
                                  </span>
                                </div>
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    </MealDropZone>
                  )
                  })}
                </div>

                <div ref={sideRailAnchorRef} className="order-1 lg:order-2 lg:self-start">
                  {desktopRailPinned ? (
                    <div aria-hidden="true" className="hidden lg:block" style={{ height: desktopRailStyle.placeholderHeight }} />
                  ) : (
                    <div className="flex flex-col gap-3 pb-8 lg:h-[calc(100vh-3rem)] lg:overflow-y-auto lg:pr-1">
                      <NutritionLibraryRail
                        libraryPanelRef={libraryPanelRef}
                        libraryFilter={libraryFilter}
                        setLibraryFilter={setLibraryFilter}
                        libraryTree={libraryTree}
                        libraryExpanded={libraryExpanded}
                        setLibraryExpanded={setLibraryExpanded}
                        filteredLibraryTree={filteredLibraryTree}
                        countLeaves={countLeaves}
                        buildLibraryExpansionState={buildLibraryExpansionState}
                      />
                      <NutritionTotalsPanel
                        nutritionTotals={nutritionTotals}
                        showTotalsMicros={showTotalsMicros}
                        setShowTotalsMicros={setShowTotalsMicros}
                      />
                    </div>
                  )}
                </div>
              </div>

              {desktopRailPinned && libraryPortalTarget
                ? createPortal(
                    <div
                      className="hidden lg:flex lg:flex-col lg:gap-3 lg:overflow-y-auto lg:pr-1"
                      style={{
                        position: 'fixed',
                        top: desktopRailStyle.top,
                        left: desktopRailStyle.left,
                        width: desktopRailStyle.width,
                        height: desktopRailStyle.height,
                        zIndex: 10,
                      }}
                    >
                      <NutritionLibraryRail
                        libraryPanelRef={libraryPanelRef}
                        libraryFilter={libraryFilter}
                        setLibraryFilter={setLibraryFilter}
                        libraryTree={libraryTree}
                        libraryExpanded={libraryExpanded}
                        setLibraryExpanded={setLibraryExpanded}
                        filteredLibraryTree={filteredLibraryTree}
                        countLeaves={countLeaves}
                        buildLibraryExpansionState={buildLibraryExpansionState}
                      />
                      <NutritionTotalsPanel
                        nutritionTotals={nutritionTotals}
                        showTotalsMicros={showTotalsMicros}
                        setShowTotalsMicros={setShowTotalsMicros}
                      />
                    </div>,
                    libraryPortalTarget
                  )
                : null}

              {libraryPortalTarget
                ? createPortal(
                    <DragOverlay style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <LibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>,
                    libraryPortalTarget
                  )
                : (
                    <DragOverlay style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeDragNode ? <LibraryDragOverlay node={activeDragNode} /> : null}
                    </DragOverlay>
                  )}

            </DndContext>
          </div>
        )}

        {!loading && activeTab === 'exercise' && (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            onDragStart={handleExerciseDragStart}
            onDragCancel={handleExerciseDragCancel}
            onDragEnd={handleExerciseDragEnd}
            autoScroll={false}
          >
            <div className="space-y-5">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
                <button
                  type="button"
                  onClick={handleCreateExercisePlan}
                  disabled={createExercisePlanLoading || !sessionId}
                  className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700 transition hover:border-emerald-300 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Plus className="h-4 w-4" />
                  {createExercisePlanLoading ? 'Adding...' : 'Add new workout'}
                </button>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handlePdfRequest('exercises')}
                    disabled={pdfLoading.exercises || !exercisePlans.length}
                    title="Print this exercise plan"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Printer className="h-4 w-4" />
                    {pdfLoading.exercises ? 'Loading…' : 'Print'}
                  </button>
                  <button
                    type="button"
                    onClick={() => exercisePlanCopy.open({ planId: null, planApiId: null, name: 'All workouts', isGlobal: true })}
                    disabled={!exercisePlans.length}
                    title="Copy every workout in this exercise plan to another program"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Copy className="h-4 w-4" />
                    Copy complete plan
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExerciseBulkOpen('session')}
                    disabled={!exercisePlans.length}
                    title="Apply values to all workout rows"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <ArrowDown className="h-4 w-4" />
                    Apply to all workouts
                  </button>
                </div>
              </div>

              <div className="grid gap-6 lg:items-start lg:grid-cols-[minmax(0,1fr),300px]">
                <div className="order-2 space-y-5 lg:order-1">
                  {(exercisePlans || []).map((plan, planIndex) => (
                    <ExerciseDropZone key={plan.id} planId={plan.id}>
                      <Card
                        className={`border-0 shadow-sm ring-1 transition-shadow duration-300 ${
                          planDragLoading[plan.id]
                            ? 'ring-2 ring-fitness-primary/40 shadow-md'
                            : 'ring-slate-200/70'
                        }`}
                      >
                        <CardHeader className="flex flex-col gap-3 space-y-0 pb-2">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <input
                              type="text"
                              value={exercisePlanNameDrafts[plan.id] ?? plan.name}
                              onChange={(e) => handleExercisePlanNameDraftChange(plan.id, e.target.value)}
                              onBlur={() => handleExercisePlanNameCommit(plan.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault()
                                  e.currentTarget.blur()
                                }
                              }}
                              disabled={Boolean(exercisePlanNameSaving[plan.id])}
                              className="min-w-[220px] max-w-[420px] rounded-md border border-transparent bg-transparent px-1 py-0.5 text-base font-semibold text-slate-900 transition hover:border-slate-200 focus:border-fitness-primary/40 focus:bg-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-70"
                            />
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleExerciseBulkOpen('plan', plan)}
                                title="Apply values to this workout"
                                className="inline-flex h-8 items-center gap-1.5 rounded-full border border-fitness-primary/30 bg-fitness-primary/10 px-3 text-xs font-semibold text-fitness-primary shadow-sm transition hover:border-fitness-primary/50 hover:bg-fitness-primary/20"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                Apply values
                              </button>
                              <button
                                type="button"
                                onClick={() => exercisePlanCopy.open({ planId: plan.id, planApiId: plan.apiId || plan.id, name: plan.name || 'Workout', isGlobal: false })}
                                title="Copy workout plan"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                              >
                                <FilePlus2 className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleExercisePlanDeleteRequest(plan)}
                                title="Delete workout plan"
                                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-200 bg-white text-red-500 transition hover:border-red-300 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Active days</span>
                            {dayOrder.map((day) => {
                              const selected = plan.days?.includes(day)
                              const saveKey = `${plan.id}-${day}`
                              const isSaving = Boolean(exerciseDaySaving[saveKey])
                              return (
                                <button
                                  type="button"
                                  key={`${plan.id}-${day}`}
                                  onClick={() => {
                                    if (isSaving) return
                                    handleExercisePlanDayToggle(plan, day, !selected)
                                  }}
                                  disabled={isSaving}
                                  className={`flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold transition ${
                                    selected
                                      ? 'border-fitness-primary/40 bg-fitness-primary/10 text-fitness-primary'
                                      : 'border-slate-200 bg-white text-slate-500 hover:border-fitness-primary/30'
                                  } ${isSaving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    readOnly
                                    className="pointer-events-none h-3 w-3 rounded border-slate-300"
                                  />
                                  {dayLabels[day]}
                                </button>
                              )
                            })}
                          </div>
                        </CardHeader>
                        {planDragLoading[plan.id] && (
                          <div className="mx-6 mb-4 overflow-hidden rounded-lg border border-fitness-primary/25 bg-fitness-primary/[0.07]">
                            <div className="flex items-center gap-2 px-3 py-2 text-xs font-semibold text-fitness-primary">
                              <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin" />
                              <span>Saving changes to this workout…</span>
                            </div>
                            <div className="relative h-1 w-full bg-fitness-primary/15">
                              <div className="animate-indeterminate-bar absolute inset-y-0 rounded-full bg-fitness-primary" />
                            </div>
                          </div>
                        )}
                        {planDragErrors[plan.id] && (
                          <div className="mx-6 mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                            <span className="flex-1">{planDragErrors[plan.id]}</span>
                            <button
                              type="button"
                              onClick={() => clearPlanDragError(plan.id)}
                              className="flex-shrink-0 rounded-md p-0.5 text-red-500 transition hover:bg-red-100 hover:text-red-700"
                              aria-label="Dismiss error"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        <CardContent className="p-3 space-y-3 pt-0">
                          <div className="overflow-x-auto overflow-y-visible rounded-xl border border-slate-200">
                            <table className="w-full min-w-[980px] table-fixed border-collapse text-sm">
                              <thead className="bg-slate-50">
                                <tr className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                  <th style={{ width: '40px' }} className="px-2 py-2.5"></th>
                                  <th style={{ width: '160px' }} className="whitespace-nowrap px-2 py-2.5 text-left">Body Part</th>
                                  <th className="whitespace-nowrap px-2 py-2.5 text-left">Name</th>
                                  <th style={{ width: '112px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Sets/Mode</th>
                                  <th style={{ width: '112px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Reps/Intensity</th>
                                  <th style={{ width: '112px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Rest/HeartRate</th>
                                  <th style={{ width: '112px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Speed/Duration</th>
                                  <th style={{ width: '156px' }} className="whitespace-nowrap px-2 py-2.5 text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100">
                                <SortableContext items={(plan.exercises || []).map((exercise) => exercise.id)} strategy={verticalListSortingStrategy}>
                                  {(plan.exercises || []).map((exercise) => {
                                    const loadingKey = `copy-${plan.id}-${exercise.id}`
                                    const isRowDeleting =
                                      exerciseDeleteLoading &&
                                      exerciseDeleteDialog?.planId === plan.id &&
                                      exerciseDeleteDialog?.exerciseId === exercise.id
                                    const rowBusy =
                                      Boolean(exerciseActionLoading[loadingKey]) ||
                                      Boolean(exerciseActionLoading[exercise.id]) ||
                                      isRowDeleting
                                    return (
                                      <SortableExerciseRow
                                        key={exercise.id}
                                        planId={plan.id}
                                        planApiId={plan.apiId || plan.id}
                                        planName={plan.name || 'Workout'}
                                        exercise={exercise}
                                        categories={exerciseCategoryOptions}
                                        nameOptions={exerciseTemplateOptionsByRow[exercise.id] || EMPTY_OPTIONS}
                                        nameOptionsLoading={Boolean(exerciseTemplateLoadingByRow[exercise.id])}
                                        onFieldChange={handleExerciseFieldChange}
                                        onFieldCommit={handleExerciseFieldCommit}
                                        onNewRowCategoryChange={handleNewExerciseRowCategoryChange}
                                        onNewRowNameChange={handleNewExerciseRowNameChange}
                                        onVideo={handleExerciseVideoOpen}
                                        onCopy={handleExerciseCopy}
                                        onInfo={handleExerciseInfoOpen}
                                        onDelete={handleExerciseDeleteRequest}
                                        disabled={rowBusy}
                                      />
                                    )
                                  })}
                                </SortableContext>
                                {!(plan.exercises || []).length && (
                                  <tr>
                                    <td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-400">
                                      No exercises yet. Add one below.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                          <div className="flex items-center justify-between gap-3">
                            <button
                              type="button"
                              onClick={() => handleAddExerciseRow(plan)}
                              className="inline-flex items-center gap-2 rounded-md border border-fitness-primary bg-white px-3 py-2 text-sm font-semibold text-fitness-primary shadow-sm ring-2 ring-fitness-primary/20 transition hover:bg-fitness-primary/5"
                            >
                              <Plus className="h-4 w-4" />
                              Add exercise
                            </button>
                            {(planIndex > 0 || planIndex < exercisePlans.length - 1) && (
                              <div className="inline-flex items-center overflow-hidden rounded-full border border-slate-200 bg-slate-50 shadow-sm">
                                {planIndex > 0 && (
                                  <button
                                    type="button"
                                    onClick={() => handleExercisePlanReorder(plan.id, 'up')}
                                    disabled={Boolean(exercisePlanReorderSaving[plan.id])}
                                    title="Move workout up"
                                    className="flex h-8 w-8 items-center justify-center text-slate-500 transition hover:bg-white hover:text-fitness-primary disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    <ChevronUp className="h-4 w-4" />
                                  </button>
                                )}
                                {planIndex > 0 && planIndex < exercisePlans.length - 1 && (
                                  <div className="h-5 w-px bg-slate-200" />
                                )}
                                {planIndex < exercisePlans.length - 1 && (
                                  <button
                                    type="button"
                                    onClick={() => handleExercisePlanReorder(plan.id, 'down')}
                                    disabled={Boolean(exercisePlanReorderSaving[plan.id])}
                                    title="Move workout down"
                                    className="flex h-8 w-8 items-center justify-center text-slate-500 transition hover:bg-white hover:text-fitness-primary disabled:cursor-not-allowed disabled:opacity-40"
                                  >
                                    <ChevronDown className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </ExerciseDropZone>
                  ))}
                </div>

                <div ref={sideRailAnchorRef} className="order-1 lg:order-2 lg:self-start">
                  {desktopRailPinned ? (
                    <div aria-hidden="true" className="hidden lg:block" style={{ height: desktopRailStyle.placeholderHeight }} />
                  ) : (
                    <div className="flex flex-col pb-8 lg:h-[calc(100vh-3rem)] lg:overflow-hidden">
                      <ExerciseLibraryRail
                        libraryPanelRef={libraryPanelRef}
                        exerciseLibraryFilter={exerciseLibraryFilter}
                        setExerciseLibraryFilter={setExerciseLibraryFilter}
                        exerciseLibraryTree={exerciseLibraryTree}
                        exerciseLibraryExpanded={exerciseLibraryExpanded}
                        setExerciseLibraryExpanded={setExerciseLibraryExpanded}
                        filteredExerciseLibraryTree={filteredExerciseLibraryTree}
                        countLeaves={countLeaves}
                        buildLibraryExpansionState={buildLibraryExpansionState}
                      />
                    </div>
                  )}
                </div>
              </div>

              {desktopRailPinned && libraryPortalTarget
                ? createPortal(
                    <div
                      className="hidden overflow-hidden lg:flex lg:flex-col"
                      style={{
                        position: 'fixed',
                        top: desktopRailStyle.top,
                        left: desktopRailStyle.left,
                        width: desktopRailStyle.width,
                        height: desktopRailStyle.height,
                        zIndex: 10,
                      }}
                    >
                      <ExerciseLibraryRail
                        libraryPanelRef={libraryPanelRef}
                        exerciseLibraryFilter={exerciseLibraryFilter}
                        setExerciseLibraryFilter={setExerciseLibraryFilter}
                        exerciseLibraryTree={exerciseLibraryTree}
                        exerciseLibraryExpanded={exerciseLibraryExpanded}
                        setExerciseLibraryExpanded={setExerciseLibraryExpanded}
                        filteredExerciseLibraryTree={filteredExerciseLibraryTree}
                        countLeaves={countLeaves}
                        buildLibraryExpansionState={buildLibraryExpansionState}
                      />
                    </div>,
                    libraryPortalTarget
                  )
                : null}

              {libraryPortalTarget
                ? createPortal(
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeExerciseDrag ? <ExerciseLibraryDragOverlay node={activeExerciseDrag} /> : null}
                    </DragOverlay>,
                    libraryPortalTarget
                  )
                : (
                    <DragOverlay dropAnimation={null} style={{ zIndex: 2147483647, pointerEvents: 'none' }}>
                      {activeExerciseDrag ? <ExerciseLibraryDragOverlay node={activeExerciseDrag} /> : null}
                    </DragOverlay>
                  )}
            </div>
          </DndContext>
        )}
      </div>


      <Dialog open={Boolean(exercisePlanCopy.dialog)} onOpenChange={(open) => (!open ? exercisePlanCopy.close() : null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{exercisePlanCopy.dialog?.isGlobal ? 'Copy complete exercise plan' : 'Copy workout'}</DialogTitle>
            <DialogDescription>
              {exercisePlanCopy.dialog?.isGlobal ? (
                'Duplicate every workout in this exercise plan into another program.'
              ) : (
                <>
                  Duplicate <span className="font-semibold text-slate-700">{exercisePlanCopy.dialog?.name || 'this workout'}</span> into another program.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <ClientCopyPicker
            copy={exercisePlanCopy}
            renderProgramLabel={(program) =>
              program.goal?.name ? `${program.goal.name} · ${program.name}` : program.name || `Program ${program.id}`
            }
            saveLabel="Copy workout"
            savingLabel="Copying..."
          />
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(planCopy.dialog)} onOpenChange={(open) => (!open ? planCopy.close() : null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Copy meal plan</DialogTitle>
            <DialogDescription>
              Duplicate <span className="font-semibold text-slate-700">{planCopy.dialog?.name || 'this meal plan'}</span> into another program.
            </DialogDescription>
          </DialogHeader>
          <ClientCopyPicker
            copy={planCopy}
            renderProgramLabel={(program) =>
              program.goal?.name ? `${program.goal.name} · ${program.name}` : program.name || `Program ${program.id}`
            }
          />
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(nutritionGlobalCopy.dialog)} onOpenChange={(open) => (!open ? nutritionGlobalCopy.close() : null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Copy complete meal plan</DialogTitle>
            <DialogDescription>Duplicate every meal plan in this program into another program.</DialogDescription>
          </DialogHeader>
          <ClientCopyPicker
            copy={nutritionGlobalCopy}
            renderProgramLabel={(program) =>
              program.goal?.name ? `${program.goal.name} · ${program.name}` : program.name || `Program ${program.id}`
            }
            saveLabel="Copy meal plans"
            savingLabel="Copying..."
          />
        </DialogContent>
      </Dialog>
      <Dialog open={!!mealCopy.dialog} onOpenChange={(open) => (!open ? mealCopy.close() : null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Copy meal</DialogTitle>
            <DialogDescription>
              Duplicate <span className="font-semibold text-slate-700">{mealCopy.dialog?.meal?.title || 'this meal'}</span> into another meal plan.
            </DialogDescription>
          </DialogHeader>
          <ClientCopyPicker
            copy={mealCopy}
            renderProgramLabel={(program) =>
              program.goal?.name ? `${program.goal.name} · ${program.name}` : program.name || `Program ${program.id}`
            }
            requireSecondary
            saveLabel="Copy meal"
            savingLabel="Copying..."
            extra={
              mealCopy.programId ? (
                <div className="space-y-2">
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Target meal plan</label>
                  <Select
                    value={mealCopy.secondarySelectedId}
                    onValueChange={(value) => mealCopy.setSecondarySelectedId(String(value))}
                    disabled={mealCopy.secondaryLoading || !mealCopy.secondaryOptions.length}
                  >
                    <SelectTrigger className="h-10 rounded-lg border-slate-200">
                      <SelectValue placeholder={mealCopy.secondaryLoading ? 'Loading meal plans...' : 'Select a meal plan'} />
                    </SelectTrigger>
                    <SelectContent>
                      {mealCopy.secondaryOptions.map((plan) => (
                        <SelectItem key={`meal-copy-plan-${plan.id}`} value={String(plan.id)}>
                          {plan.name || `Meal plan ${plan.id}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {!mealCopy.secondaryLoading && !mealCopy.secondaryOptions.length && (
                    <p className="text-xs text-slate-500">No meal plans found for this program.</p>
                  )}
                </div>
              ) : null
            }
          />
        </DialogContent>
      </Dialog>

      <SharedDialogs
        printDialogOpen={printDialogOpen}
        setPrintDialogOpen={setPrintDialogOpen}
        printSelection={printSelection}
        setPrintSelection={setPrintSelection}
        printLoading={printLoading}
        handlePrintSubmit={handlePrintSubmit}
        deleteDialog={deleteDialog}
        setDeleteDialog={setDeleteDialog}
        actionLoading={actionLoading}
        measurementsLoading={measurementsLoading}
        confirmDeleteComposition={confirmDeleteComposition}
      />

      <ExerciseDialogs
        exerciseVideoDialog={exerciseVideoDialog}
        handleExerciseVideoClose={handleExerciseVideoClose}
        exerciseInfoDialog={exerciseInfoDialog}
        setExerciseInfoDialog={setExerciseInfoDialog}
        exerciseDeleteDialog={exerciseDeleteDialog}
        setExerciseDeleteDialog={setExerciseDeleteDialog}
        exerciseDeleteLoading={exerciseDeleteLoading}
        handleConfirmExerciseDelete={handleConfirmExerciseDelete}
        exercisePlanDeleteDialog={exercisePlanDeleteDialog}
        setExercisePlanDeleteDialog={setExercisePlanDeleteDialog}
        exercisePlanDeleteLoading={exercisePlanDeleteLoading}
        handleExercisePlanDeleteConfirm={handleExercisePlanDeleteConfirm}
        exerciseBulkDialog={exerciseBulkDialog}
        exerciseBulkValues={exerciseBulkValues}
        setExerciseBulkValues={setExerciseBulkValues}
        exerciseBulkError={exerciseBulkError}
        exerciseBulkSaving={exerciseBulkSaving}
        handleExerciseBulkClose={handleExerciseBulkClose}
        handleExerciseBulkSave={handleExerciseBulkSave}
      />

      <NutritionDialogs
        mealNoteDialog={mealNoteDialog}
        mealNoteDraft={mealNoteDraft}
        setMealNoteDraft={setMealNoteDraft}
        mealNoteError={mealNoteError}
        mealNoteSaving={mealNoteSaving}
        handleMealNoteClose={handleMealNoteClose}
        handleMealNoteSave={handleMealNoteSave}
        planDeleteDialog={planDeleteDialog}
        setPlanDeleteDialog={setPlanDeleteDialog}
        planDeleteLoading={planDeleteLoading}
        handlePlanDeleteConfirm={handlePlanDeleteConfirm}
        mealPlanDeleteDialog={mealPlanDeleteDialog}
        setMealPlanDeleteDialog={setMealPlanDeleteDialog}
        mealPlanDeleteLoading={mealPlanDeleteLoading}
        handleConfirmMealPlanDelete={handleConfirmMealPlanDelete}
        mealDeleteDialog={mealDeleteDialog}
        setMealDeleteDialog={setMealDeleteDialog}
        mealDeleteLoading={mealDeleteLoading}
        handleConfirmMealDelete={handleConfirmMealDelete}
      />
    </div>
  )
}

export default SessionDetail